import React, { useCallback, useEffect, useMemo, useRef, useState, Component } from 'react';
import { X, Banknote, CreditCard, Smartphone, Loader2, Receipt, CheckSquare, Square, AlertCircle, CheckCircle2, Mail, Printer, ArrowLeft, FileText, Minus, Plus, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { useTableOrderItems } from '../../lib/table-hooks';
import { supabase } from '../../lib/supabase';
import { useBillingInfo, processPayment, processTablePaymentAtomic, openPrintableInvoice, PaymentItemSnapshot, PaymentRecord, BillingInfo } from '../../lib/billing-hooks';
import { TableWithOrder } from '../../lib/types';
// ─── DEPLOY CHECK (temporal) ────────────────────────────────────────
// Se imprime UNA vez al cargar el bundle. Si tras hard-refresh
// (Ctrl+Shift+R) este log NO aparece en la consola, producción todavía
// está sirviendo el bundle viejo (problema de build/caché, no de código).
// Quitar este marcador una vez confirmado el deploy.
console.log('[NOIZA][DEPLOY_CHECK] invoice-customer-payload-v2 ACTIVE');
interface PayTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableWithOrder | null;
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  createdBy?: string | null;
}
type PayMethod = 'cash' | 'sinpe' | 'card';
type Step = 'select' | 'paid' | 'invoice';
// ─── Helper: división equitativa con partes pagadas bloqueadas ────────
// Devuelve un array de longitud N con el monto (entero) de cada parte.
// Las partes ya pagadas conservan su monto real (paidAmounts); el resto
// del total se reparte equitativamente entre las partes NO pagadas, y el
// remanente de redondeo cae en la última parte no pagada. Garantiza que
// la suma de TODAS las partes sea exactamente igual a unitPrice.
function computeEqualSplit(unitPrice: number, N: number, paidParts: Set<number>, paidAmounts: Map<number, number>): number[] {
  const total = Math.round(unitPrice);
  const arr = new Array<number>(N).fill(0);
  let paidSum = 0;
  paidParts.forEach(p => {
    if (p >= 0 && p < N) {
      const amt = Math.round(paidAmounts.get(p) ?? 0);
      arr[p] = amt;
      paidSum += amt;
    }
  });
  const unpaid: number[] = [];
  for (let k = 0; k < N; k++) {
    if (!paidParts.has(k)) unpaid.push(k);
  }
  const budget = Math.max(0, total - paidSum);
  if (unpaid.length > 0) {
    const base = Math.floor(budget / unpaid.length);
    const rem = budget - base * unpaid.length;
    unpaid.forEach((idx, i) => {
      arr[idx] = base + (i === unpaid.length - 1 ? rem : 0);
    });
  }
  return arr;
}
function ArmaPriceInput({
  value,
  onChange,
  needsPrice,
  itemName
}: {
  value: string;
  onChange: (val: string) => void;
  needsPrice: boolean;
  itemName: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <>
      <button type="button" onClick={() => setIsOpen(true)} className={cn('relative w-28 pl-5 pr-2 py-1.5 rounded-md border-2 text-sm font-black tabular-nums text-right focus:outline-none transition-colors', needsPrice ? 'border-red-300 focus:border-red-500 text-red-700 bg-white' : 'border-amber-300 focus:border-amber-500 text-gray-900 bg-white')} aria-label={`Precio de ${itemName}`} data-id="element-1440" data-mp="2c3h51ill2j3d">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-black text-amber-600 pointer-events-none" data-id="element-1441" data-mp="ab223820zsf1">
          ₡
        </span>
        {value || <span className={needsPrice ? 'text-red-300' : 'text-gray-300'} data-id="element-1442" data-mp="5hz6kudou8w7">
            Monto
          </span>}
      </button>

      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-id="element-1443" data-mp="3spt045vd7x5l">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xs overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-1444" data-mp="3non6m5tvs5v0">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between" data-id="element-1445" data-mp="1mn7asuz8w7a5">
              <h3 className="font-bold text-gray-700 text-sm truncate pr-4" data-id="element-1446" data-mp="1mpjp8y5snzig">
                Monto: {itemName}
              </h3>
              <button type="button" onClick={() => setIsOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1447" data-mp="1cy5suz953ftj">
                <X className="w-5 h-5" data-id="element-1448" data-mp="13vedwph221x5" />
              </button>
            </div>
            <div className="p-6" data-id="element-1449" data-mp="18wk8ksu9zz9b">
              <div className="text-center mb-6" data-id="element-1450" data-mp="18tpo51b9iefw">
                <div className="text-3xl font-black text-gray-900 tabular-nums tracking-tight" data-id="element-1451" data-mp="tzba7f6k1v83">
                  ₡ {value || '0'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-1452" data-mp="2ocsrikgd9vuo">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} type="button" onClick={() => onChange(value + num.toString())} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1453" data-mp="2tbvyiafwqc5a">
                    {num}
                  </button>)}
                <button type="button" onClick={() => onChange('')} className="h-14 rounded-2xl bg-red-50 hover:bg-red-100 active:bg-red-200 text-sm font-bold text-red-600 transition-colors shadow-sm border border-red-100 flex items-center justify-center" data-id="element-1454" data-mp="mlsmpbs4co5n">
                  Borrar
                </button>
                <button type="button" onClick={() => onChange(value + '0')} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1455" data-mp="15dlc6w7zbr8m">
                  0
                </button>
                <button type="button" onClick={() => onChange(value + '00')} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-lg font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1456" data-mp="1b4fye8lgs4eg">
                  00
                </button>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} className="w-full mt-4 h-14 rounded-2xl bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold text-lg transition-colors shadow-md" data-id="element-1457" data-mp="1u0frjmq6hdzm">
                Aceptar
              </button>
            </div>
          </div>
        </div>}
    </>;
}
export function PayTableModal({
  isOpen,
  onClose,
  table,
  restaurantId,
  onSuccess,
  createdBy
}: PayTableModalProps) {
  const orderId = table?.current_order?.id;
  // Pass orderId as a fallback so marketplace orders (no real table) can
  // still load their items for Cobrar/Facturar.
  const {
    items,
    loading: itemsLoading,
    refetch
  } = useTableOrderItems(table?.id ? String(table.id) : undefined, restaurantId ? String(restaurantId) : undefined, orderId ? String(orderId) : undefined);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurantId);
  const [step, setStep] = useState<Step>('select');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // El método de pago arranca SIN seleccionar (null). El sistema NO asume
  // "Efectivo" — obliga al cajero a elegir uno antes de poder cobrar.
  const [method, setMethod] = useState<PayMethod | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedPayment, setCompletedPayment] = useState<PaymentRecord | null>(null);
  const [allPaid, setAllPaid] = useState(false);
  // ─── Auto-send invoice result ─────────────────────────────────────────
  // Cuando el cliente activa "Solicitar factura" + "Enviar ahora" y hay
  // correo, se invoca la edge function send-invoice-email INMEDIATAMENTE
  // después de insertar el payment. El resultado se guarda acá para
  // mostrar feedback en el paso "Pago registrado". Si falla, el flujo
  // cae al paso 'invoice' clásico para que el cajero pueda reintentar
  // manualmente — el pago ya quedó registrado.
  const [autoInvoiceResult, setAutoInvoiceResult] = useState<{
    ok: boolean;
    msg: string;
    email: string;
  } | null>(null);
  const [autoInvoiceSending, setAutoInvoiceSending] = useState(false);
  // ─── Invoice toggle + customer data (collected BEFORE payment) ─────────
  // El usuario puede activar "Solicitar factura" desde el primer paso para
  // ingresar nombre / cédula / correo. Si está activo, después de cobrar
  // saltamos directo al paso de factura (sin pasar por el confirm "paid").
  const [wantsInvoice, setWantsInvoice] = useState(false);
  // sendLater: cuando wantsInvoice=true, el cajero puede elegir "Enviar
  // ahora" (flujo actual → salta al paso de factura) o "Enviar más tarde"
  // (guarda los datos en orders.wants_invoice + invoice_* y la factura
  // queda pendiente en "Otras Herramientas → Enviar Facturas").
  const [sendLater, setSendLater] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerLegalId, setCustomerLegalId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  // ─── Imprimir comprobante con datos del cliente (NatuCafé) ─────────
  // Independiente del envío de factura por correo. Cuando está activo,
  // el print job incluye show_customer_on_invoice=true + nombre + cédula
  // para que el bridge los imprima. Cuando está inactivo, la factura
  // impresa sale EXACTAMENTE igual que hoy (sin datos del cliente).
  const [showCustomerOnInvoice, setShowCustomerOnInvoice] = useState(false);
  // ─── Estado DEDICADO del comprobante impreso (NatuCafé id 19) ──────
  // CRÍTICO: estos dos estados son EXCLUSIVOS del bloque "Imprimir
  // comprobante con datos del cliente". NO se reutiliza `customerName`
  // (que en mesas representa "Mesa 01" / el form de correo) para evitar
  // que el payload de la factura impresa arrastre el nombre de la mesa.
  // Los inputs del comprobante y el payload de invoice se controlan
  // SOLO con estos estados.
  const [invoiceCustomerName, setInvoiceCustomerName] = useState('');
  const [invoiceCustomerId, setInvoiceCustomerId] = useState('');
  // ─── Discount (universal — works for all KDS) ──────────────────────
  // El cajero puede asignar un descuento al cobro completo (no por línea).
  // Se aplica como una línea sintética negativa al snapshot del pago, lo
  // que hace que aparezca automáticamente en el ticket impreso
  // (openPrintableInvoice), en el correo de factura
  // (send-invoice-email itera items_json) y en el ticket térmico del
  // bridge NatuCafé (format_invoice_ticket_natucafe itera payload.items).
  // Además persistimos el monto en orders.discount_amount.
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  // ─── Quantity-based partial pay ────────────────────────────────────
  // Cada línea de order_items tiene quantity N. Permitimos cobrar una
  // cantidad parcial dentro de la línea (ej: 2 de 3 capuccinos). El
  // saldo restante queda disponible para próximos cobros.
  //
  // paidQtyMap: cuántas unidades de cada order_item ya se cobraron en
  // pagos anteriores (suma de payments.items_json[].quantity).
  // selectedQty: cuántas unidades de cada order_item se van a cobrar
  // en ESTE pago. Default = remaining_quantity al seleccionar.
  const [paidQtyMap, setPaidQtyMap] = useState<Map<string, number>>(new Map());
  const [selectedQty, setSelectedQty] = useState<Map<string, number>>(new Map());
  // ─── Split a single shareable item ─────────────────────────────────
  const [persistedSplits, setPersistedSplits] = useState<Map<string, {
    N: number;
    paidParts: Set<number>;
    // Monto real cobrado por cada parte ya pagada (index → ₡).
    // Necesario para recalcular correctamente el restante de las
    // partes NO pagadas cuando el cajero edita montos manualmente.
    paidAmounts: Map<number, number>;
  }>>(new Map());
  const [splitModeMap, setSplitModeMap] = useState<Map<string, number>>(new Map());
  const [splitSelectedMap, setSplitSelectedMap] = useState<Map<string, Set<number>>>(new Map());
  // Monto editable de cada parte por order_item (array de longitud N).
  // Default = división equitativa; el cajero puede sobreescribir cada
  // parte no pagada y el restante se reparte automáticamente.
  const [splitAmountsMap, setSplitAmountsMap] = useState<Map<string, number[]>>(new Map());
  // ─── "Arma tu plato" — PRECIO INDIVIDUAL por cada línea ───────────
  // Cada línea de "Arma tu plato" (menu_item_id == null) es un ítem
  // independiente: el cajero le asigna su propio monto y elige cuáles
  // cobrar. armaPriceMap guarda el texto crudo del input por
  // order_item_id (se parsea a número en getArmaPriceNum).
  const [armaPriceMap, setArmaPriceMap] = useState<Map<string, string>>(new Map());
  const setArmaPrice = useCallback((id: string, value: string) => {
    const clean = value.replace(/[^0-9.]/g, '');
    setArmaPriceMap(prev => {
      const nm = new Map(prev);
      nm.set(id, clean);
      return nm;
    });
  }, []);
  // Carga la suma de cantidades ya cobradas por order_item desde la tabla
  // payments. Es seguro de re-correr — solo lee.
  const refetchPaidQty = useCallback(async () => {
    if (!orderId || !restaurantId) {
      setPaidQtyMap(new Map());
      setPersistedSplits(new Map());
      return;
    }
    try {
      const {
        data: pastPayments,
        error
      } = await supabase.from('payments').select('items_json').eq('order_id', orderId);
      if (error) {
        console.warn('[NOIZA] paidQty fetch error:', error);
        setPaidQtyMap(new Map());
        setPersistedSplits(new Map());
        return;
      }
      const map = new Map<string, number>();
      const splitMap = new Map<string, {
        N: number;
        paidParts: Set<number>;
        paidAmounts: Map<number, number>;
      }>();
      for (const p of pastPayments || []) {
        const arr = (p as any).items_json || [];
        for (const it of arr) {
          if (!it?.order_item_id) continue;
          // FIX 2026-08-17 (Rancho / Mesa 8 / orden 4154): payments.items_json
          // guarda order_item_id como string ("12060"), mientras
          // order_items.id puede llegar como number. Map.get distingue
          // string de number, así que sin normalizar acá las unidades ya
          // pagadas por unidad quedaban ignoradas.
          const orderItemKey = String(it.order_item_id);
          if (orderItemKey === '__discount__') continue;
          const prev = map.get(orderItemKey) || 0;
          map.set(orderItemKey, prev + (Number(it.quantity) || 0));
          if (it.notes && typeof it.notes === 'string') {
            const match = it.notes.match(/Parte (\d+)\/(\d+)/);
            if (match) {
              const k = parseInt(match[1], 10) - 1; // 0-indexed
              const N = parseInt(match[2], 10);
              if (!splitMap.has(orderItemKey)) {
                splitMap.set(orderItemKey, {
                  N,
                  paidParts: new Set(),
                  paidAmounts: new Map()
                });
              }
              const entry = splitMap.get(orderItemKey)!;
              entry.paidParts.add(k);
              // Guardamos el monto real cobrado de esa parte (line_total).
              entry.paidAmounts.set(k, Math.round(Number(it.line_total) || 0));
            }
          }
        }
      }
      setPaidQtyMap(map);
      setPersistedSplits(splitMap);
    } catch (err) {
      console.warn('[NOIZA] paidQty fetch exception:', err);
      setPaidQtyMap(new Map());
      setPersistedSplits(new Map());
    }
  }, [orderId, restaurantId]);
  useEffect(() => {
    if (isOpen) refetchPaidQty();
  }, [isOpen, refetchPaidQty, items.length]);
  // Side-fetch order_items.menu_item_id keyed by order_item_id. We can't
  // rely on v_table_order_items.menu_item_id because the view doesn't
  // expose that column — which makes every item look like an arma item.
  // This map is the source of truth for arma detection in the UI.
  const [menuItemIdMap, setMenuItemIdMap] = useState<Map<string, string | null>>(new Map());
  useEffect(() => {
    let cancelled = false;
    async function loadMenuItemIds() {
      if (!orderId) {
        setMenuItemIdMap(new Map());
        return;
      }
      try {
        const {
          data,
          error
        } = await supabase.from('order_items').select('id, menu_item_id').eq('order_id', orderId);
        if (cancelled) return;
        if (error) {
          console.warn('[NOIZA] menu_item_id map fetch error:', error);
          setMenuItemIdMap(new Map());
          return;
        }
        const map = new Map<string, string | null>();
        (data || []).forEach((row: any) => {
          map.set(String(row.id), row.menu_item_id ?? null);
        });
        setMenuItemIdMap(map);
      } catch (e) {
        if (cancelled) return;
        console.warn('[NOIZA] menu_item_id map fetch exception:', e);
        setMenuItemIdMap(new Map());
      }
    }
    if (isOpen) loadMenuItemIds();
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId, items.length]);
  // Items con quantity disponible para cobrar (remaining > 0). NOTA:
  // si paid_at ya está seteado, el ítem ya fue completamente pagado
  // por el flujo viejo (que marca paid_at al instante en cobro total) —
  // lo respetamos y lo dejamos fuera. Para los que tienen paid_at NULL
  // calculamos remaining = quantity - paid_so_far.
  const unpaidItems = useMemo(() => {
    return items.filter((i: any) => !i.paid_at).map((i: any) => {
      const paidSoFar = paidQtyMap.get(String(i.order_item_id)) || 0;
      const remaining = Math.max(0, (i.quantity || 0) - paidSoFar);
      return {
        ...i,
        paid_quantity: paidSoFar,
        remaining_quantity: remaining
      };
    }).filter((i: any) => i.remaining_quantity > 0);
  }, [items, paidQtyMap]);
  // ─── BLINDAJE: precio por unidad CON recargos de opciones ──────────
  // El `unit_price` de la línea es el precio BASE del producto
  // (unit_price_snapshot) y NO incluye el sobreprecio de opciones como
  // "Costilla", "patacones extra", etc. Ese recargo SÍ está guardado en
  // `line_total` (lo calcula create-order.ts como (base + opciones) * qty).
  //
  // Si cobramos usando `unit_price` perdemos el recargo en la factura —
  // exactamente el bug de Rancho con "casado con costilla". Acá derivamos
  // el precio por unidad REAL desde el line_total persistido:
  //   precio_unitario_real = line_total / quantity
  //
  // Para productos SIN recargo, line_total/quantity == unit_price, así que
  // el resultado es idéntico al de hoy (cero cambio de comportamiento).
  // Las líneas "Arma tu plato" no pasan por acá (usan su precio manual).
  // IMPORTANTE: se define ANTES de cualquier efecto/función que lo use
  // para evitar el "temporal dead zone" (effUnitPrice is not defined).
  const effUnitPrice = useCallback((i: any): number => {
    const qty = Number(i?.quantity) || 0;
    const lt = Number(i?.line_total);
    if (qty > 0 && Number.isFinite(lt) && lt > 0) {
      return lt / qty;
    }
    return Number(i?.unit_price) || 0;
  }, []);
  // FIX 2026-08-16 (incidente Mesa 3 / orden 4074): antes este efecto
  // dependia de `unpaidItems.length`, que NO cambia cuando la cantidad
  // pendiente de un item que sigue en la lista se reduce por un pago
  // parcial ya registrado (ej: un item con 3 unidades donde ya se cobro
  // 1). Eso dejaba `selectedQty` con el valor viejo (mayor al remanente
  // real) y el monto enviado a `expected_amount` quedaba inflado,
  // provocando el 409/40001 "la cuenta cambio". Ver
  // FIX_v_restaurant_tables_cashier / GATES_1-4 para el analisis
  // completo.
  //
  // Se separa en dos efectos: uno de seleccion inicial (solo al abrir),
  // y uno de reconciliacion que corre cada vez que cambia la firma real
  // del estado pendiente (cantidad original, pagada, remanente,
  // paid_at, pagos asociados via paidQtyMap) — no solo el largo del
  // arreglo.
  useEffect(() => {
    if (isOpen && unpaidItems.length > 0 && step === 'select') {
      // Por defecto seleccionar TODOS los ítems con su remaining
      // completo. El cajero puede ajustar con [-]/[+] dentro de cada
      // línea o deseleccionar líneas completas.
      setSelectedIds(new Set(unpaidItems.map((i: any) => i.order_item_id)));
      setSelectedQty(new Map(unpaidItems.map((i: any) => [i.order_item_id, i.remaining_quantity] as [string, number])));
      const newSplitModeMap = new Map<string, number>();
      const newSplitSelectedMap = new Map<string, Set<number>>();
      const newSplitAmountsMap = new Map<string, number[]>();
      persistedSplits.forEach((val, key) => {
        newSplitModeMap.set(key, val.N);
        const selectedParts = new Set<number>();
        for (let k = 0; k < val.N; k++) {
          if (!val.paidParts.has(k)) selectedParts.add(k);
        }
        newSplitSelectedMap.set(key, selectedParts);
        // Reconstruimos los montos de cada parte: las pagadas conservan
        // su monto real (paidAmounts), las no pagadas se reparten el
        // saldo restante equitativamente.
        const itm = unpaidItems.find((i: any) => i.order_item_id === key);
        const unitPrice = itm ? effUnitPrice(itm) : 0;
        newSplitAmountsMap.set(key, computeEqualSplit(unitPrice, val.N, val.paidParts, val.paidAmounts));
      });
      setSplitModeMap(newSplitModeMap);
      setSplitSelectedMap(newSplitSelectedMap);
      setSplitAmountsMap(newSplitAmountsMap);
    }
    if (!isOpen) {
      setStep('select');
      setSelectedIds(new Set());
      setSelectedQty(new Map());
      setSplitModeMap(new Map());
      setSplitSelectedMap(new Map());
      setSplitAmountsMap(new Map());
      setError(null);
      setMethod(null);
      setCompletedPayment(null);
      setAllPaid(false);
      setWantsInvoice(false);
      setSendLater(false);
      setCustomerName('');
      setCustomerLegalId('');
      setCustomerEmail('');
      setShowCustomerOnInvoice(false);
      setInvoiceCustomerName('');
      setInvoiceCustomerId('');
      setAutoInvoiceResult(null);
      setAutoInvoiceSending(false);
      setArmaPriceMap(new Map());
      setPrintComandaResult(null);
      setPrintingComanda(false);
      setDiscountAmount(0);
      setShowDiscountPanel(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Firma estable del estado pendiente real: cambia si cambia la
  // cantidad remanente de CUALQUIER item ya visible, no solo si entra o
  // sale un item de la lista.
  const unpaidSignature = useMemo(() => unpaidItems.map((i: any) => `${i.order_item_id}:${i.remaining_quantity}`).sort().join('|'), [unpaidItems]);

  // Reconciliacion: NO pisa la seleccion manual del cajero. Solo quita
  // ids que ya no tienen remanente (se pagaron/anularon en otro pago) y
  // recorta selectedQty que quedo por encima del remanente real. Nunca
  // sube una cantidad por encima de lo que el cajero eligio a mano.
  useEffect(() => {
    if (!isOpen || step !== 'select') return;
    const remainingById = new Map(unpaidItems.map((i: any) => [i.order_item_id, i.remaining_quantity]));
    setSelectedIds(prev => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (remainingById.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSelectedQty(prev => {
      let changed = false;
      const next = new Map(prev);
      prev.forEach((qty, id) => {
        const remaining = remainingById.get(id);
        if (remaining === undefined) {
          next.delete(id);
          changed = true;
        } else if (qty > remaining) {
          next.set(id, remaining);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unpaidSignature, isOpen, step]);
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Al re-seleccionar, restablece la cantidad a remaining
        const itm = unpaidItems.find((i: any) => i.order_item_id === id);
        if (itm) {
          setSelectedQty(m => {
            const nm = new Map(m);
            nm.set(id, itm.remaining_quantity);
            return nm;
          });
        }
      }
      return next;
    });
  };
  const setQtyForItem = (id: string, newQty: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    if (!itm) return;
    const clamped = Math.max(1, Math.min(newQty, itm.remaining_quantity));
    setSelectedQty(m => {
      const nm = new Map(m);
      nm.set(id, clamped);
      return nm;
    });
  };
  // ─── Edición manual del monto de cada parte de un ítem dividido ─────
  // Reglas:
  //  · No negativos, no mayor al saldo disponible para partes no pagadas.
  //  · Al editar una parte, el restante se reparte automáticamente entre
  //    las OTRAS partes NO pagadas (remanente de redondeo a la última).
  //  · Las partes ya pagadas quedan bloqueadas y conservan su monto.
  //  · La suma de TODAS las partes siempre == total del ítem.
  const setPartAmount = (id: string, k: number, rawValue: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    if (!itm) return;
    const N = splitModeMap.get(id);
    if (!N) return;
    const persisted = persistedSplits.get(id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    // No se puede editar una parte ya pagada.
    if (paidParts.has(k)) return;
    // Usamos effUnitPrice para incluir el recargo de opciones (Rancho).
    const unitPrice = Math.round(effUnitPrice(itm));
    let paidSum = 0;
    paidParts.forEach(p => {
      paidSum += Math.round(paidAmounts.get(p) ?? 0);
    });
    const unpaidBudget = Math.max(0, unitPrice - paidSum);
    const val = Math.max(0, Math.min(Math.round(rawValue || 0), unpaidBudget));
    setSplitAmountsMap(prev => {
      const nm = new Map(prev);
      const current = prev.get(id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
      const next = [...current];
      // Aseguramos que las partes pagadas mantengan su monto real.
      paidParts.forEach(p => {
        next[p] = Math.round(paidAmounts.get(p) ?? next[p] ?? 0);
      });
      next[k] = val;
      // Otras partes NO pagadas (distintas de k) entre las que repartir.
      const otherUnpaid: number[] = [];
      for (let idx = 0; idx < N; idx++) {
        if (idx !== k && !paidParts.has(idx)) otherUnpaid.push(idx);
      }
      const remaining = Math.max(0, unpaidBudget - val);
      if (otherUnpaid.length === 0) {
        // Esta es la única parte no pagada: debe cubrir todo el saldo.
        next[k] = unpaidBudget;
      } else {
        const base = Math.floor(remaining / otherUnpaid.length);
        const rem = remaining - base * otherUnpaid.length;
        otherUnpaid.forEach((idx, i) => {
          next[idx] = base + (i === otherUnpaid.length - 1 ? rem : 0);
        });
      }
      nm.set(id, next);
      return nm;
    });
  };
  // Inicializa/recalcula el array de montos en división equitativa para
  // un ítem (usado al activar "Dividir" o al cambiar la cantidad N).
  const seedEqualSplit = (id: string, N: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    const unitPrice = itm ? effUnitPrice(itm) : 0;
    const persisted = persistedSplits.get(id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    setSplitAmountsMap(prev => {
      const nm = new Map(prev);
      nm.set(id, computeEqualSplit(unitPrice, N, paidParts, paidAmounts));
      return nm;
    });
  };
  const allSelected = useMemo(() => {
    if (unpaidItems.length === 0) return false;
    return unpaidItems.every((i: any) => {
      if (splitModeMap.has(i.order_item_id)) {
        const N = splitModeMap.get(i.order_item_id)!;
        const selectedParts = splitSelectedMap.get(i.order_item_id) || new Set();
        const persistedSplit = persistedSplits.get(i.order_item_id);
        const paidCount = persistedSplit?.paidParts.size || 0;
        return selectedParts.size + paidCount === N;
      }
      const qty = selectedQty.get(i.order_item_id) ?? i.remaining_quantity;
      return selectedIds.has(i.order_item_id) && qty === i.remaining_quantity;
    });
  }, [unpaidItems, selectedIds, selectedQty, splitModeMap, splitSelectedMap, persistedSplits]);
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      const newSplitSelectedMap = new Map<string, Set<number>>();
      splitModeMap.forEach((N, id) => {
        newSplitSelectedMap.set(id, new Set());
      });
      setSplitSelectedMap(newSplitSelectedMap);
    } else {
      setSelectedIds(new Set(unpaidItems.map((i: any) => i.order_item_id)));
      setSelectedQty(new Map(unpaidItems.map((i: any) => [i.order_item_id, i.remaining_quantity] as [string, number])));
      const newSplitSelectedMap = new Map<string, Set<number>>();
      splitModeMap.forEach((N, id) => {
        const persistedSplit = persistedSplits.get(id);
        const parts = new Set<number>();
        for (let k = 0; k < N; k++) {
          if (!persistedSplit?.paidParts.has(k)) {
            parts.add(k);
          }
        }
        newSplitSelectedMap.set(id, parts);
      });
      setSplitSelectedMap(newSplitSelectedMap);
    }
  };
  // selectedItems lleva ahora la cantidad efectiva a cobrar y el
  // line_total recalculado en base a la cantidad seleccionada (no la
  // total de la línea original).
  // Precio numérico individual de una línea "Arma tu plato".
  const getArmaPriceNum = useCallback((id: string) => Number(String(armaPriceMap.get(id) ?? '').replace(/[^0-9.]/g, '')) || 0, [armaPriceMap]);
  // ─── Helper used everywhere the UI classifies arma items.
  //     Authoritative source: menuItemIdMap from order_items table.
  const isArmaItem = useCallback((orderItemId: string) => {
    const realMenuItemId = menuItemIdMap.get(String(orderItemId));
    // Only treat as arma when we KNOW the menu_item_id is null. If the
    // map hasn't loaded yet (undefined), default to NOT arma so we don't
    // misclassify normal items as arma during the brief loading window.
    return realMenuItemId === null;
  }, [menuItemIdMap]);
  const normalSelectedItems = unpaidItems.filter((i: any) => selectedIds.has(i.order_item_id) && !splitModeMap.has(i.order_item_id)).map((i: any) => {
    const qty = selectedQty.get(i.order_item_id) ?? i.remaining_quantity;
    const isArma = isArmaItem(i.order_item_id);
    // Cada línea "Arma tu plato" usa su propio precio individual.
    const armaPrice = isArma ? getArmaPriceNum(i.order_item_id) : 0;
    // Para ítems normales usamos el precio por unidad CON recargos de
    // opciones (derivado del line_total persistido), no el precio base.
    const effectiveUnitPrice = isArma ? armaPrice : effUnitPrice(i);
    // BLINDAJE: redondeamos la línea a colones enteros para que cada
    // renglón impreso sea limpio y la suma de líneas == el monto
    // sellado en processPayment (sin "polvo" de punto flotante al
    // derivar effUnitPrice = line_total / quantity). Para precios
    // enteros (el 99% de los casos) el resultado es idéntico.
    const lineTotal = Math.round(effectiveUnitPrice * qty);
    return {
      ...i,
      is_arma_item: isArma,
      arma_price: isArma ? armaPrice : null,
      effective_unit_price: effectiveUnitPrice,
      pay_quantity: qty,
      pay_line_total: lineTotal
    };
  });
  const splitSelectedItems = unpaidItems.filter((i: any) => splitModeMap.has(i.order_item_id)).flatMap((i: any) => {
    const N = splitModeMap.get(i.order_item_id)!;
    const selectedParts = splitSelectedMap.get(i.order_item_id) || new Set();
    if (selectedParts.size === 0) return [];
    // Precio por unidad CON recargos de opciones (ver effUnitPrice).
    const unitPrice = effUnitPrice(i);
    const persisted = persistedSplits.get(i.order_item_id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    const amounts = splitAmountsMap.get(i.order_item_id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
    return Array.from(selectedParts).map(partIndex => {
      const partPrice = Math.round(amounts[partIndex] ?? 0);
      return {
        ...i,
        is_arma_item: false,
        arma_price: null,
        effective_unit_price: unitPrice,
        pay_quantity: 1 / N,
        pay_line_total: partPrice,
        notes: `Parte ${partIndex + 1}/${N}`,
        is_split_part: true,
        split_part_index: partIndex
      };
    });
  });
  const selectedItems = [...normalSelectedItems, ...splitSelectedItems];
  const hasSelectedArmaItems = selectedItems.some((i: any) => i.is_arma_item);
  // Bloqueamos el cobro si CUALQUIER línea arma seleccionada no tiene
  // un precio individual válido (> 0).
  const hasIncompleteArmaPrices = selectedItems.some((i: any) => i.is_arma_item && (i.effective_unit_price || 0) <= 0);
  const total = selectedItems.reduce((s: number, i: any) => s + (i.pay_line_total || 0), 0);
  // Total final = subtotal de los ítems - descuento (clamp en 0 para
  // evitar negativos si el cajero pone un descuento mayor al total).
  const totalAfterDiscount = Math.max(0, total - (discountAmount || 0));
  // ─── Validación de división manual ──────────────────────────────
  // Para cada ítem en modo división, la suma de TODAS las partes
  // (pagadas + no pagadas) debe ser exactamente igual al total del
  // ítem, sin negativos y sin que una parte supere el total. Si algo
  // no cuadra, bloqueamos el cobro.
  const hasInvalidSplit = useMemo(() => {
    for (const [id, N] of splitModeMap.entries()) {
      const itm = unpaidItems.find((i: any) => i.order_item_id === id);
      if (!itm) continue;
      const unitPrice = Math.round(effUnitPrice(itm));
      const persisted = persistedSplits.get(id);
      const paidParts = persisted?.paidParts ?? new Set<number>();
      const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
      const amounts = splitAmountsMap.get(id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
      let sum = 0;
      for (let k = 0; k < N; k++) {
        const v = Math.round(amounts[k] ?? 0);
        if (v < 0 || v > unitPrice) return true;
        sum += v;
      }
      if (sum !== unitPrice) return true;
    }
    return false;
  }, [splitModeMap, splitAmountsMap, persistedSplits, unpaidItems]);
  // isPartial = no todas las líneas están seleccionadas, O alguna línea
  // se está cobrando con quantity < remaining. En cualquier caso la
  // orden queda con saldo pendiente.
  const isPartial = useMemo(() => {
    const selectedQtyMap = new Map<string, number>();
    for (const item of selectedItems) {
      const prev = selectedQtyMap.get(item.order_item_id) || 0;
      selectedQtyMap.set(item.order_item_id, prev + (item.pay_quantity || 0));
    }
    for (const unpaid of unpaidItems) {
      const payingQty = selectedQtyMap.get(unpaid.order_item_id) || 0;
      // Usamos un pequeño epsilon (0.001) para tolerar errores de
      // redondeo en punto flotante al sumar fracciones (ej: 1/3 + 1/3 + 1/3).
      if (unpaid.remaining_quantity - payingQty > 0.001) {
        return true;
      }
    }
    return false;
  }, [selectedItems, unpaidItems]);
  // ─── Idempotencia del cobro ───────────────────────────────────────
  // El UUID del pago se genera UNA sola vez al confirmar y se reusa en
  // cualquier reintento (red intermitente, doble clic, error 40001).
  // La RPC lo usa como llave: si el pago ya quedó registrado, devuelve
  // el mismo comprobante en lugar de cobrar dos veces.
  const paymentIdRef = useRef<string | null>(null);
  const handleSubmit = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la mesa.');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Selecciona al menos un ítem para cobrar.');
      return;
    }
    // ─── Método de pago OBLIGATORIO ──────────────────────────────────
    // El sistema NO asume el método. El cajero debe elegir Efectivo,
    // SINPE o Tarjeta explícitamente antes de cobrar.
    if (!method) {
      setError('Selecciona el método de pago (Efectivo, SINPE o Tarjeta).');
      return;
    }
    if (hasIncompleteArmaPrices) {
      // Abort submission if there are incomplete arma prices
      setError('Ingresa el precio del plato "Arma tu plato" antes de cobrar.');
      return;
    }
    if (hasInvalidSplit) {
      setError('La suma de las partes debe ser exactamente igual al total del ítem.');
      return;
    }
    // ─── NatuCafé (id 19): comprobante con datos del cliente ──────────
    // Si el cajero activó "Imprimir comprobante con datos del cliente"
    // pero NO escribió el nombre, NO permitimos cobrar — el comprobante
    // saldría en blanco. Solo aplica a NatuCafé; el resto de
    // restaurantes no se ve afectado.
    if (String(restaurantId) === '19' && showCustomerOnInvoice && !invoiceCustomerName.trim()) {
      setError('Escribí el nombre del cliente para imprimir el comprobante.');
      return;
    }
    setError(null);
    setSubmitting(true);
    // Persist arma item prices and update order totals.
    // Cada línea "Arma tu plato" tiene su propio precio individual:
    // persistimos el monto que el cajero asignó a cada una.
    try {
      const armaItemsToPersist = selectedItems.filter((ai: any) => ai.is_arma_item);
      for (const ai of armaItemsToPersist) {
        const unitPrice = ai.effective_unit_price ?? 0;
        await supabase.from('order_items').update({
          unit_price_snapshot: unitPrice,
          line_total: unitPrice * (ai.quantity || 1)
        }).eq('id', ai.order_item_id);
      }
      const {
        data: freshOrderItems
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', orderId);
      const newSubtotal = (freshOrderItems || []).filter((oi: any) => oi.status !== 'cancelled').reduce((sum: number, oi: any) => sum + (Number(oi.line_total) || 0), 0);
      const {
        data: orderData
      } = await supabase.from('orders').select('delivery_fee').eq('id', orderId).single();
      const deliveryFee = orderData?.delivery_fee as number || 0;
      const newTotal = newSubtotal + deliveryFee;
      await supabase.from('orders').update({
        subtotal: newSubtotal,
        total: newTotal,
        updated_at: new Date().toISOString()
      }).eq('id', orderId);
    } catch (err) {
      console.warn('[NOIZA] error updating arma prices and totals:', err);
      // Non-blocking, do not throw
    }
    // Snapshot lleva pay_quantity (no la quantity original) y
    // pay_line_total como line_total. Así la factura/comprobante muestra
    // exactamente lo que se cobró.
    const snapshot: PaymentItemSnapshot[] = selectedItems.map((i: any) => ({
      order_item_id: i.order_item_id,
      item_name: i.item_name,
      quantity: i.pay_quantity,
      unit_price: i.effective_unit_price ?? i.unit_price,
      line_total: i.pay_line_total,
      notes: i.notes || null
    }));
    // ─── Descuento universal ──────────────────────────────────────
    // Se inyecta como una línea sintética negativa al final del
    // snapshot. El ID '__discount__' es inocuo en el loop de
    // paidQtyMap (no matchea ningún order_items.id real) y se
    // renderiza automáticamente en:
    //  · openPrintableInvoice → iterador de items_json
    //  · send-invoice-email   → iterador de items_json
    //  · NatuCafé bridge      → format_invoice_ticket_natucafe
    if ((discountAmount || 0) > 0) {
      snapshot.push({
        order_item_id: '__discount__',
        item_name: 'Descuento',
        quantity: 1,
        unit_price: -(discountAmount || 0),
        line_total: -(discountAmount || 0),
        notes: null
      } as PaymentItemSnapshot);
    }
    // ─── Elegir el camino de cobro ────────────────────────────────
    // El cobro atómico (RPC) solo acepta órdenes dine_in ligadas a una
    // MESA real: es la que resuelve el caso "el dinero entró pero la
    // mesa quedó abierta". Todo lo que no tiene mesa —venta rápida de
    // Estantería, delivery, pickup, pedidos del marketplace— sigue por
    // el flujo anterior, que ya funcionaba bien y no tiene mesa que
    // liberar. Así ambos conviven sin pisarse.
    //
    // Ojo: la venta rápida de Estantería se guarda con
    // delivery_type='dine_in' pero SIN table_id, así que no basta con
    // mirar el tipo — hay que exigir mesa real.
    const orderDeliveryType = (table as any)?.current_order?.delivery_type ?? null;
    const hasRealTable = !!table?.id && !String(table?.table_number || '').startsWith('order:');
    const isDineInTableOrder = hasRealTable && (orderDeliveryType === null || orderDeliveryType === undefined || orderDeliveryType === 'dine_in');
    const chargeLegacy = () => processPayment({
      restaurantId,
      orderId,
      tableId: table?.id || null,
      amount: totalAfterDiscount,
      method,
      items: snapshot,
      isPartial,
      createdBy: createdBy || null
    });
    let result: {
      success: boolean;
      payment?: PaymentRecord;
      error?: string;
      conflict?: boolean;
    };
    if (isDineInTableOrder) {
      // Cobro atómico: consecutivo, pago, líneas marcadas y cierre de la
      // orden ocurren dentro de la misma transacción.
      if (!paymentIdRef.current) {
        paymentIdRef.current = crypto.randomUUID();
      }
      result = await processTablePaymentAtomic({
        restaurantId,
        orderId,
        tableId: table?.id || null,
        method,
        expectedAmount: totalAfterDiscount,
        payAllUnpaid: !isPartial,
        // `snapshot` ya trae la línea `__discount__` y la `quantity`
        // parcial de cada ítem — la RPC no necesita nada más.
        items: snapshot,
        paymentId: paymentIdRef.current
      });
      // Red de seguridad: si la RPC rechaza la orden por no ser dine_in
      // con mesa, cobramos por el flujo anterior en vez de dejar al
      // cajero trabado. La RPC no registró nada en ese caso.
      if (!result.success && /dine_in/i.test(String(result.error || ''))) {
        console.warn('[NOIZA][billing] RPC rechazó la orden (no dine_in con mesa) — usando flujo anterior', {
          order_id: orderId
        });
        paymentIdRef.current = null;
        result = await chargeLegacy();
      }
    } else {
      // Sin mesa (Estantería, delivery, pickup): flujo original intacto.
      result = await chargeLegacy();
    }
    setSubmitting(false);
    if (!result.success || !result.payment) {
      if (result.conflict) {
        // La cuenta cambió mientras el modal estaba abierto (se agregó o
        // se anuló algo). Refrescamos el detalle y pedimos confirmar el
        // saldo otra vez. NO se ejecuta ninguno de los pasos antiguos.
        await refetch();
        await refetchPaidQty();
      }
      setError(result.error || 'Error al procesar el pago.');
      return;
    }
    // Cobro confirmado: el próximo cobro (ej. la otra mitad de una
    // cuenta dividida) debe usar un UUID nuevo.
    paymentIdRef.current = null;
    await refetch();
    await refetchPaidQty();
    // ─── Persistir descuento en orders.discount_amount ────────────
    // Sumamos al valor previo por si en cobros parciales se asignaron
    // descuentos en distintos pagos. Si la columna no existe en el
    // schema (otros restaurantes), el error se loguea y seguimos:
    // el pago ya quedó registrado.
    if ((discountAmount || 0) > 0 && orderId) {
      try {
        const {
          data: ordRow
        } = await supabase.from('orders').select('discount_amount').eq('id', orderId).single();
        const prevDiscount = Number((ordRow as any)?.discount_amount) || 0;
        const {
          error: discErr
        } = await supabase.from('orders').update({
          discount_amount: prevDiscount + (discountAmount || 0)
        }).eq('id', orderId);
        if (discErr) {
          console.warn('[NOIZA][discount] update failed:', discErr);
        }
      } catch (e) {
        console.warn('[NOIZA][discount] update exception:', e);
      }
    }
    // ─── NatuCafé (id 19) — Auto-print de factura ──────────────────
    // Insertamos un job pending en order_print_jobs con
    // target_printer_type='invoice' para que el bridge dual lo mande
    // a la impresora de facturas configurada en
    // restaurant_printer_settings. SOLO NatuCafé — Rancho y demás
    // restaurantes mantienen su flujo intacto. Nunca bloqueamos al
    // cajero: si la inserción falla, loguemos y seguimos.
    // Flag de verificación dura del comprobante (solo NatuCafé invoice).
    let natucafeInvoiceVerifyFailed = false;
    if (String(restaurantId) === '19' && orderId) {
      try {
        const {
          data: orderRow
        } = await supabase.from('orders').select('tracking_token, order_number, customer_name, customer_phone, delivery_type, table_id, notes, subtotal, delivery_fee, total, source, created_at').eq('id', orderId).single();
        // ⚠️ FIX 6 (NatuCafé — cobro dividido / factura parcial 2026-06-05):
        // El payload de la factura impresa debe contener EXACTAMENTE los
        // ítems (y cantidades) que el cajero seleccionó para este pago —
        // NO toda la orden. Antes re-consultábamos todos los order_items
        // de la orden, así que en un split-bill el cliente que pagó su
        // parte recibía una factura con TODA la cuenta. Ahora derivamos
        // el payload del MISMO `selectedItems` que alimenta el snapshot
        // del pago (pay_quantity / pay_line_total / effective_unit_price),
        // de modo que la factura impresa coincide línea por línea y en
        // monto con lo seleccionado en Caja y con el comprobante web.
        //
        // Los ítems cancelados ya están excluidos por construcción: solo
        // los ítems pagables (unpaidItems) llegan a selectedItems.
        const selectedOrderItemIds = selectedItems.map((i: any) => i.order_item_id).filter((id: any) => id && id !== '__discount__');
        const optionsByItemId: Record<string, any[]> = {};
        if (selectedOrderItemIds.length > 0) {
          const {
            data: optRows
          } = await supabase.from('order_item_options').select('order_item_id, option_group, option_name, price_delta').in('order_item_id', selectedOrderItemIds);
          (optRows || []).forEach((o: any) => {
            if (!optionsByItemId[o.order_item_id]) optionsByItemId[o.order_item_id] = [];
            optionsByItemId[o.order_item_id].push({
              option_group: o.option_group,
              option_name: o.option_name,
              price_delta: o.price_delta
            });
          });
        }
        const itemsPayload = selectedItems.map((i: any) => ({
          menu_item_id: menuItemIdMap.get(String(i.order_item_id)) ?? null,
          item_name_snapshot: i.item_name,
          unit_price_snapshot: i.effective_unit_price ?? i.unit_price,
          quantity: i.pay_quantity,
          notes: i.notes || null,
          station_code: i.station_code ?? null,
          line_total: i.pay_line_total,
          selected_options: optionsByItemId[i.order_item_id] || []
        }));
        // Append descuento sintético al payload del ticket de factura
        // para que aparezca como línea negativa en el térmico.
        if ((discountAmount || 0) > 0) {
          itemsPayload.push({
            menu_item_id: null,
            item_name_snapshot: 'Descuento',
            unit_price_snapshot: -(discountAmount || 0),
            quantity: 1,
            notes: null,
            station_code: null,
            line_total: -(discountAmount || 0),
            selected_options: []
          });
        }
        // ─── FACTURA POR CADA PAGO (cobro dividido) ──────────────────
        // CONTRATO (NatuCafé id 19): este bloque corre en CADA pago que
        // registra el cajero — parcial o total. NO está condicionado a
        // que la mesa quede saldada. Por lo tanto:
        //   · Cada pago parcial genera SU PROPIO print job de factura.
        //   · `items` = SOLO las líneas/partes pagadas en ESTE pago
        //     (derivadas de `selectedItems`, con su pay_quantity /
        //     pay_line_total), nunca toda la orden.
        //   · `total` = EXACTAMENTE el monto de ESTE pago
        //     (`totalAfterDiscount`), que es el mismo `amount` que se
        //     pasó a processPayment → la factura impresa y el cobro
        //     coinciden al céntimo.
        //   · `created_at` = AHORA, para que cada factura parcial salga
        //     con la hora real de su pago (no la de creación de la orden).
        //   · `trigger` único por payment.id → el UNIQUE INDEX de dedupe
        //     (order_id, payload->>'trigger') NO bloquea los siguientes
        //     pagos: cada pago tiene su propio job.
        // Al finalizar la mesa NO se imprime ninguna factura adicional
        // por el total completo: este bloque es el ÚNICO creador de
        // facturas de NatuCafé y siempre factura por pago.
        //
        // ─── Datos del cliente del comprobante (estado DEDICADO) ─────
        // Se calculan JUSTO antes de armar el payload con los valores
        // ACTUALES de los inputs del comprobante. NO usamos customerName
        // (mesa / correo) para nada de la factura impresa.
        const cleanInvoiceCustomerName = showCustomerOnInvoice ? invoiceCustomerName.trim() : '';
        const cleanInvoiceCustomerId = showCustomerOnInvoice ? invoiceCustomerId.trim() : '';
        const invoicePayload = {
          trigger: `invoice_payment_${result.payment.id}`,
          payment_id: result.payment.id,
          // `is_partial` (interno) + `is_partial_invoice` (alias del
          // contrato pedido): ambos indican si este pago dejó saldo
          // pendiente. El bridge no los necesita para imprimir (usa
          // items + total), pero los dejamos para auditoría / reportes.
          is_partial: isPartial,
          is_partial_invoice: isPartial,
          payment_method: method,
          order_id: orderId,
          order_number: orderRow?.order_number,
          // Fallback seguro pedido para la factura NatuCafé: si el cajero
          // activó el checkbox y escribió nombre, ese nombre manda; si no,
          // cae al customer_name de la orden (ej. "Mesa 05"); nunca null.
          customer_name: cleanInvoiceCustomerName || orderRow?.customer_name || '',
          customer_phone: orderRow?.customer_phone,
          delivery_type: orderRow?.delivery_type,
          table_id: orderRow?.table_id,
          notes: orderRow?.notes,
          // Subtotal de la PARTE pagada (suma de los ítems seleccionados
          // antes del descuento), NO de la orden completa. delivery_fee
          // va en 0 porque el monto cobrado es solo de las líneas
          // seleccionadas (en dine-in no se cobra envío por persona).
          subtotal: total,
          delivery_fee: 0,
          total: totalAfterDiscount,
          // `paid_amount` = monto EXACTO cobrado en ESTE pago (== total
          // de la parte, == payments.amount). Es lo que se imprime en
          // la factura de este pago, nunca el total de la mesa.
          paid_amount: totalAfterDiscount,
          discount_amount: discountAmount || 0,
          source: orderRow?.source,
          created_at: new Date().toISOString(),
          items: itemsPayload,
          // ─── Datos del cliente en el comprobante impreso ───────────
          // Gated por el checkbox "Imprimir comprobante con datos del
          // cliente". El bridge SOLO imprime estos datos cuando
          // show_customer_on_invoice === true; si es false la factura
          // sale idéntica a hoy. invoice_customer_id se envía también
          // como customer_id_number por compatibilidad con el bridge.
          // Datos del cliente para el comprobante impreso. El bridge lee
          // estos campos con payload.get(...). Enviamos SIEMPRE strings
          // definidos (nunca null) para que el .get() del bridge reciba
          // un valor concreto. Regla: si el checkbox está activo y hay
          // texto, viaja el dato; si está apagado o vacío, viaja '' →
          // el bridge imprime "Cliente:" y "Cedula" en blanco igual que hoy.
          show_customer_on_invoice: showCustomerOnInvoice,
          invoice_customer_name: cleanInvoiceCustomerName,
          invoice_customer_id: cleanInvoiceCustomerId,
          // Alias de compatibilidad con el bridge (mismo valor, string).
          customer_id_number: cleanInvoiceCustomerId,
          // ─── DEBUG TEMPORAL — identificar el origen del print job ──
          // Si el último invoice job en order_print_jobs NO trae estos
          // campos, fue creado por otro lugar (trigger / edge / código
          // viejo), no por este componente. Quitar tras el diagnóstico.
          debug_source: 'PayTableModal',
          debug_version: 'invoice-customer-payload-v2',
          debug_created_at: new Date().toISOString()
        };
        const payloadFactura = invoicePayload;
        // ── LOG FINAL ANTES DEL INSERT ──────────────────────────────
        console.log('[NOIZA][NATUCAFE][INVOICE_PAYLOAD_FINAL]', payloadFactura);
        const {
          data: insertedJob,
          error: pjError
        } = await supabase.from('order_print_jobs').insert({
          restaurant_id: 19,
          order_id: orderRow?.tracking_token || null,
          order_number: orderRow?.order_number,
          status: 'pending',
          target_printer_type: 'invoice',
          payload: payloadFactura
        }).select('id, restaurant_id, target_printer_type, status, payload').maybeSingle();
        if (pjError) {
          console.warn('[NOIZA][NatuCafé][invoice-print] insert failed:', pjError);
        }
        // ── LOG DEL JOB REALMENTE INSERTADO ─────────────────────────
        console.log('[NOIZA][NATUCAFE][INVOICE_JOB_INSERTED]', insertedJob);
        // ── VERIFICACIÓN DURA ───────────────────────────────────────
        // Si el cajero pidió datos del cliente pero el job quedó
        // guardado SIN nombre, abortamos: NO marcamos el pago como
        // completado y mostramos el error. Solo NatuCafé + invoice.
        if (showCustomerOnInvoice && insertedJob && insertedJob.target_printer_type === 'invoice' && !String((insertedJob.payload as any)?.invoice_customer_name || '').trim()) {
          console.error('[NOIZA][NATUCAFE][INVOICE_JOB_INSERTED] invoice_customer_name vacío pese a showCustomerOnInvoice=true', insertedJob);
          natucafeInvoiceVerifyFailed = true;
        }
      } catch (printErr) {
        console.warn('[NOIZA][NatuCafé][invoice-print] exception:', printErr);
      }
    }
    // Si la verificación dura falló, NO avanzamos a "pagado": dejamos
    // al cajero corregir el nombre y reintentar la impresión.
    if (natucafeInvoiceVerifyFailed) {
      setSubmitting(false);
      setError('El comprobante no guardó el nombre del cliente. Verificá el nombre e intentá cobrar de nuevo.');
      return;
    }
    // allPaid = todas las líneas se cobraron completas en este pago Y
    // no quedan otras líneas pendientes.
    const allPaidNow = !isPartial;
    setCompletedPayment(result.payment);
    setAllPaid(allPaidNow);
    // ─── "Enviar más tarde": guardamos los datos del cliente en la
    // orden y marcamos wants_invoice=true para que aparezca en
    // "Otras Herramientas → Enviar Facturas". NO bloqueamos al cajero
    // si esto falla; el pago ya quedó registrado.
    if (wantsInvoice && sendLater) {
      try {
        // Guardamos los datos del cliente Y pre-linkeamos el payment.id
        // recién creado. invoice_sent_at queda en NULL (todavía no se
        // envió el correo). Al pre-linkear invoice_payment_id, la página
        // "Enviar Facturas" sabe que el pago YA existe y NO debe crear
        // uno nuevo — solo llamar a la edge function para mandar el correo.
        const {
          error: invErr
        } = await supabase.from('orders').update({
          wants_invoice: true,
          invoice_customer_name: customerName.trim() || null,
          invoice_legal_id: customerLegalId.trim() || null,
          invoice_email: customerEmail.trim() || null,
          invoice_payment_id: result.payment.id
        }).eq('id', orderId);
        if (invErr) {
          console.warn('[NOIZA][invoice] error guardando solicitud para más tarde:', invErr);
        }
      } catch (err) {
        console.warn('[NOIZA][invoice] exception send-later update:', err);
      }
      setStep('paid');
      onSuccess(allPaidNow);
      return;
    }
    // ─── Auto-envío de factura por correo (Resend vía edge function) ───
    // Si el cliente activó "Solicitar factura" + "Enviar ahora" y dejó
    // un correo, llamamos a send-invoice-email AHORA mismo, recién
    // creado el payment. Esto evita el click manual y garantiza que
    // Resend reciba la invocación. Si falla, el cajero todavía puede
    // reintentar manualmente desde el paso 'invoice'.
    const trimmedEmail = customerEmail.trim();
    if (wantsInvoice && !sendLater && trimmedEmail) {
      const paymentId = result.payment.id;
      console.log('[invoice] calling send-invoice-email');
      console.log('[invoice] payment_id', paymentId);
      setAutoInvoiceSending(true);
      try {
        const {
          data,
          error: invokeError
        } = await supabase.functions.invoke('send-invoice-email', {
          body: {
            payment_id: paymentId,
            customer_email: trimmedEmail,
            customer_name: customerName.trim() || null,
            customer_legal_id: customerLegalId.trim() || null
          }
        });
        console.log('[invoice] response', data);
        if (invokeError) {
          console.log('[invoice] error', invokeError);
          let serverError = invokeError.message || 'Error invocando la función';
          try {
            const ctx: any = (invokeError as any).context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              serverError = body?.error || body?.message || serverError;
            } else if (ctx?.error) {
              serverError = ctx.error;
            }
          } catch {
            /* swallow */
          }
          setAutoInvoiceResult({
            ok: false,
            msg: `No se pudo enviar la factura por correo: ${serverError}. Podés reintentarlo manualmente.`,
            email: trimmedEmail
          });
          setAutoInvoiceSending(false);
          // Fallback: caer al paso 'invoice' para que el cajero reintente.
          setStep('invoice');
          onSuccess(allPaidNow);
          return;
        }
        if (data?.success) {
          setAutoInvoiceResult({
            ok: true,
            msg: `Factura enviada a ${trimmedEmail}.`,
            email: trimmedEmail
          });
          setAutoInvoiceSending(false);
          // Éxito: vamos directo al paso 'paid' con el banner de
          // confirmación. El cajero NO necesita hacer nada más.
          setStep('paid');
          onSuccess(allPaidNow);
          return;
        }
        // Respuesta inesperada de la edge function (sin error pero sin success)
        console.log('[invoice] error', data);
        setAutoInvoiceResult({
          ok: false,
          msg: `Respuesta inesperada del servidor. Podés reintentarlo manualmente.`,
          email: trimmedEmail
        });
        setAutoInvoiceSending(false);
        setStep('invoice');
        onSuccess(allPaidNow);
        return;
      } catch (err: any) {
        console.log('[invoice] error', err);
        setAutoInvoiceResult({
          ok: false,
          msg: `Error al enviar la factura: ${err?.message || 'desconocido'}. Podés reintentarlo manualmente.`,
          email: trimmedEmail
        });
        setAutoInvoiceSending(false);
        setStep('invoice');
        onSuccess(allPaidNow);
        return;
      }
    }
    setStep(wantsInvoice ? 'invoice' : 'paid');
    onSuccess(allPaidNow);
  };
  // ─── Rancho San Francisco — Imprimir comanda desde PaidStep ─────────
  // Movido desde el modal de la mesa: el botón ahora vive en la segunda
  // pantalla (pago confirmado) y reemplaza "Sí, generar factura" para
  // Rancho. Sólo aplica a restaurant_id 28 — cero impacto en el resto.
  const isRancho = String(restaurantId ?? '') === '28';
  const [printingComanda, setPrintingComanda] = useState(false);
  const [printComandaResult, setPrintComandaResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  const handlePrintComanda = useCallback(async () => {
    if (!isRancho) return;
    if (!orderId) {
      setPrintComandaResult({
        ok: false,
        msg: 'Orden sin identificador.'
      });
      return;
    }
    setPrintingComanda(true);
    setPrintComandaResult(null);
    try {
      // 1) Fetch items + options para enriquecer item_name_snapshot con
      //    las opciones seleccionadas y marcar los ítems de "Arma tu plato"
      //    con prefijo [Arma]. Mismo patrón que TableManagementPage.
      let enrichedItems: any[] = [];
      try {
        const {
          data: rows
        } = await supabase.from('order_items').select('*').eq('order_id', orderId);
        // ⚠️ FIX 5 (Rancho audit 2026-05-31): filtrar items cancelados
        // ANTES de armar el payload del print job. Cuando el cajero
        // imprime la comanda manual desde el paso "paid" y previamente
        // hubo modificaciones (Modificar/Cancelar), las filas con
        // status='cancelled' siguen en la DB como historial. Sin este
        // filtro, el bridge imprimiría líneas que el cajero ya quitó
        // → cocina prepara items fantasma → pérdida. Mismo patrón de
        // filtro que useTableOrderItems (Capa 1).
        const rawItems = (rows || []).filter((r: any) => !['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(r.status || '').toLowerCase().trim()));
        if (rawItems.length > 0) {
          const itemIds = rawItems.map((it: any) => it.id);
          const {
            data: opts
          } = await supabase.from('order_item_options').select('*').in('order_item_id', itemIds);
          const byItem: Record<string, any[]> = {};
          (opts || []).forEach((o: any) => {
            const k = String(o.order_item_id);
            if (!byItem[k]) byItem[k] = [];
            byItem[k].push(o);
          });
          enrichedItems = rawItems.map((it: any) => {
            const itemOptions = byItem[String(it.id)] || [];
            const baseName = String(it.item_name_snapshot || '').trim();
            const optsSummary = itemOptions.map((o: any) => String(o.option_name_snapshot || o.option_name || '').trim()).filter((s: string) => s.length > 0).join(' · ');
            const isArma = it.menu_item_id == null;
            const armaPrefix = isArma ? '[Arma] ' : '';
            const enrichedName = optsSummary ? `${armaPrefix}${baseName} · ${optsSummary}` : `${armaPrefix}${baseName}`;
            return {
              ...it,
              options: itemOptions,
              is_arma_tu_plato: isArma,
              original_item_name: baseName,
              item_name_snapshot: enrichedName
            };
          });
        }
      } catch (e) {
        console.warn('[RANCHO_PRINT][paid] items/options fetch non-blocking:', e);
      }
      // Resolve tracking_token (UUID) — order_print_jobs.order_id es UUID.
      let orderRefUuid: string | null = null;
      try {
        const {
          data: row
        } = await supabase.from('orders').select('tracking_token').eq('id', orderId).maybeSingle();
        orderRefUuid = (row as any)?.tracking_token ?? null;
      } catch {
        /* swallow */
      }
      if (!orderRefUuid) orderRefUuid = orderId as string;
      const triggerValue = `manual_paid_${Date.now()}`;
      const order = table?.current_order as any;
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order?.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: orderId,
          order_number: order?.order_number ?? null,
          created_at: order?.created_at ?? null,
          customer_name: table?.table_number ? `Mesa ${table.table_number}` : 'Cliente',
          customer_phone: null,
          delivery_type: 'dine_in',
          payment_method: order?.payment_method ?? 'cash',
          table_number: table?.table_number ?? null,
          subtotal: order?.subtotal ?? 0,
          delivery_fee: 0,
          total: order?.total ?? 0,
          notes: order?.notes ?? null,
          items: enrichedItems,
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][paid] insert error:', error);
        setPrintComandaResult({
          ok: false,
          msg: `No se pudo imprimir: ${error.message || 'error desconocido'}`
        });
      } else {
        setPrintComandaResult({
          ok: true,
          msg: '🖨 Enviado a la impresora'
        });
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][paid] exception:', e);
      setPrintComandaResult({
        ok: false,
        msg: e?.message || 'Error al enviar a la impresora'
      });
    } finally {
      setPrintingComanda(false);
    }
  }, [isRancho, orderId, table]);
  if (!isOpen || !table) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-1458" data-mp="fjtg5cfa6ycd">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" data-id="element-1459" data-mp="jkn3a3kwg947">
        {step === 'select' && <SelectStep table={table} billingInfo={billingInfo} infoLoading={infoLoading} itemsLoading={itemsLoading} unpaidItems={unpaidItems} selectedIds={selectedIds} allSelected={allSelected} toggleAll={toggleAll} toggleItem={toggleItem}
      // Quantity-based partial pay
      selectedQty={selectedQty} setQtyForItem={setQtyForItem}
      // Split a single shareable item
      persistedSplits={persistedSplits} splitModeMap={splitModeMap} setSplitModeMap={setSplitModeMap} splitSelectedMap={splitSelectedMap} setSplitSelectedMap={setSplitSelectedMap} splitAmountsMap={splitAmountsMap} setPartAmount={setPartAmount} seedEqualSplit={seedEqualSplit} hasInvalidSplit={hasInvalidSplit} method={method} setMethod={setMethod} error={error} isPartial={isPartial} total={total} selectedItems={selectedItems} submitting={submitting} onClose={onClose} onSubmit={handleSubmit} restaurantId={restaurantId} onSuccess={onSuccess} wantsInvoice={wantsInvoice} setWantsInvoice={setWantsInvoice} sendLater={sendLater} setSendLater={setSendLater} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} showCustomerOnInvoice={showCustomerOnInvoice} setShowCustomerOnInvoice={setShowCustomerOnInvoice} invoiceCustomerName={invoiceCustomerName} setInvoiceCustomerName={setInvoiceCustomerName} invoiceCustomerId={invoiceCustomerId} setInvoiceCustomerId={setInvoiceCustomerId} armaPriceMap={armaPriceMap} setArmaPrice={setArmaPrice} getArmaPriceNum={getArmaPriceNum} hasIncompleteArmaPrices={hasIncompleteArmaPrices} isArmaItem={isArmaItem} discountAmount={discountAmount} setDiscountAmount={setDiscountAmount} showDiscountPanel={showDiscountPanel} setShowDiscountPanel={setShowDiscountPanel} totalAfterDiscount={totalAfterDiscount} data-id="element-1460" data-mp="oiolkmai44em" />}

        {step === 'paid' && completedPayment && <PaidStep payment={completedPayment} allPaid={allPaid} autoInvoiceResult={autoInvoiceResult} autoInvoiceSending={autoInvoiceSending} isRancho={isRancho} onPrintComanda={handlePrintComanda} printingComanda={printingComanda} printComandaResult={printComandaResult} onClose={onClose} onGoInvoice={() => setStep('invoice')} onChargeRemaining={() => {
        setStep('select');
        setCompletedPayment(null);
        setSelectedIds(new Set());
      }} data-id="element-1461" data-mp="3tb38ams9yxdl" />}

        {step === 'invoice' && completedPayment && billingInfo && <InvoiceStep table={table} billingInfo={billingInfo} payment={completedPayment} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} onBack={() => setStep('paid')} onClose={onClose} data-id="element-1462" data-mp="1py3xtabyavf7" />}

        {step === 'invoice' && completedPayment && !billingInfo && <div className="p-8 text-center" data-id="element-1463" data-mp="33za0owhf3yyr">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4" data-id="element-1464" data-mp="2z0yviz22icmv">
              <AlertCircle className="w-7 h-7 text-amber-600" data-id="element-1465" data-mp="2uzn3ynzmvhc1" />
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2" data-id="element-1466" data-mp="3rpmkp5pxtm5p">
              Faltan datos de facturación
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto leading-relaxed" data-id="element-1467" data-mp="3mq1bm2h1zxf9">
              El restaurante aún no tiene configurados sus datos fiscales (razón
              social, cédula, régimen tributario). Sin estos datos no podemos
              emitir comprobantes legales.
            </p>
            <p className="text-xs text-gray-500 mb-6" data-id="element-1468" data-mp="eazn6oh421jq">
              El cobro ya quedó registrado. Para habilitar la emisión de
              facturas corré los scripts{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1469" data-mp="1s710n03ptx4p">
                ADD_BILLING_LEGAL_NAME.sql
              </code>{' '}
              y{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1470" data-mp="1x6dvo84qqaid">
                SETUP_NATUCAFE_BILLING_INFO.sql
              </code>{' '}
              en Supabase.
            </p>
            <Button onClick={onClose} className="bg-gray-900 hover:bg-gray-800 text-white font-bold" data-id="element-1471" data-mp="267btg8ol5lsi">
              Entendido
            </Button>
          </div>}
      </div>
    </div>;
}
// ─── STEP 1: Select items + payment method ────────────────────────────────
function SelectStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo | null;
  infoLoading: boolean;
  itemsLoading: boolean;
  unpaidItems: any[];
  selectedIds: Set<string>;
  allSelected: boolean;
  toggleAll: () => void;
  toggleItem: (id: string) => void;
  // Quantity-based partial pay
  selectedQty: Map<string, number>;
  setQtyForItem: (id: string, newQty: number) => void;
  // Split a single shareable item
  persistedSplits: Map<string, {
    N: number;
    paidParts: Set<number>;
    paidAmounts: Map<number, number>;
  }>;
  splitModeMap: Map<string, number>;
  setSplitModeMap: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  splitSelectedMap: Map<string, Set<number>>;
  setSplitSelectedMap: React.Dispatch<React.SetStateAction<Map<string, Set<number>>>>;
  splitAmountsMap: Map<string, number[]>;
  setPartAmount: (id: string, k: number, rawValue: number) => void;
  seedEqualSplit: (id: string, N: number) => void;
  hasInvalidSplit: boolean;
  // null = el cajero aún no eligió método (selección obligatoria).
  method: 'cash' | 'sinpe' | 'card' | null;
  setMethod: (m: 'cash' | 'sinpe' | 'card') => void;
  error: string | null;
  isPartial: boolean;
  total: number;
  selectedItems: any[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  // Necesarios para el botón "Cerrar y liberar mesa" (mesa fantasma).
  // SelectStep es un componente separado de PayTableModal, así que debe
  // recibir restaurantId y onSuccess explícitamente — de lo contrario
  // handleFreeGhostTable referencia identificadores fuera de scope y la
  // pantalla de cobro crashea con "restaurantId is not defined".
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  // Invoice (factura) — collected BEFORE payment
  wantsInvoice: boolean;
  setWantsInvoice: (b: boolean) => void;
  sendLater: boolean;
  setSendLater: (b: boolean) => void;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  showCustomerOnInvoice: boolean;
  setShowCustomerOnInvoice: (b: boolean) => void;
  // NatuCafé — estado dedicado del comprobante impreso (NO reutiliza
  // customerName/customerLegalId del form de correo).
  invoiceCustomerName: string;
  setInvoiceCustomerName: (s: string) => void;
  invoiceCustomerId: string;
  setInvoiceCustomerId: (s: string) => void;
  // Rancho — "Arma tu plato": UN SOLO monto para todo el grupo
  armaPriceMap: Map<string, string>;
  setArmaPrice: (id: string, value: string) => void;
  getArmaPriceNum: (id: string) => number;
  hasIncompleteArmaPrices: boolean;
  isArmaItem: (id: string) => boolean;
  // Discount (universal)
  discountAmount: number;
  setDiscountAmount: React.Dispatch<React.SetStateAction<number>>;
  showDiscountPanel: boolean;
  setShowDiscountPanel: React.Dispatch<React.SetStateAction<boolean>>;
  totalAfterDiscount: number;
}) {
  const {
    table,
    itemsLoading,
    unpaidItems,
    selectedIds,
    allSelected,
    toggleAll,
    toggleItem,
    selectedQty,
    setQtyForItem,
    persistedSplits,
    splitModeMap,
    setSplitModeMap,
    splitSelectedMap,
    setSplitSelectedMap,
    splitAmountsMap,
    setPartAmount,
    seedEqualSplit,
    hasInvalidSplit,
    method,
    setMethod,
    error,
    isPartial,
    total,
    selectedItems,
    submitting,
    onClose,
    onSubmit,
    restaurantId,
    onSuccess,
    wantsInvoice,
    setWantsInvoice,
    sendLater,
    setSendLater,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    showCustomerOnInvoice,
    setShowCustomerOnInvoice,
    invoiceCustomerName,
    setInvoiceCustomerName,
    invoiceCustomerId,
    setInvoiceCustomerId,
    armaPriceMap,
    setArmaPrice,
    getArmaPriceNum,
    hasIncompleteArmaPrices,
    isArmaItem,
    discountAmount,
    setDiscountAmount,
    showDiscountPanel,
    setShowDiscountPanel,
    totalAfterDiscount
  } = props;
  // ─── BLINDAJE (local a SelectStep): precio por unidad CON recargos ──
  // SelectStep es un componente separado de PayTableModal, así que NO
  // tiene acceso al effUnitPrice del padre. Definimos una copia local
  // pura (sin dependencias) para incluir el recargo de opciones de
  // Rancho (casado con costilla, patacones extra) en lo que se muestra.
  // Misma lógica: line_total/quantity, con fallback a unit_price.
  const effUnitPrice = (i: any): number => {
    const qty = Number(i?.quantity) || 0;
    const lt = Number(i?.line_total);
    if (qty > 0 && Number.isFinite(lt) && lt > 0) {
      return lt / qty;
    }
    return Number(i?.unit_price) || 0;
  };
  // ─── Cash received → change calculator (only shown when method = cash) ───
  const [cashReceived, setCashReceived] = useState<string>('');
  // Reset cash field whenever method changes or total changes meaningfully
  useEffect(() => {
    if (method !== 'cash') setCashReceived('');
  }, [method]);
  const cashReceivedNum = parseFloat(cashReceived.replace(/[^0-9.]/g, '')) || 0;
  const change = cashReceivedNum - totalAfterDiscount;
  const hasEnoughCash = cashReceivedNum >= totalAfterDiscount && cashReceivedNum > 0;
  const quickCashAmounts = useMemo(() => {
    if (totalAfterDiscount <= 0) return [] as number[];
    // Round up to nearest 1 000, 5 000, 10 000, 20 000 — only keep those >= total
    const candidates = [Math.ceil(totalAfterDiscount / 1000) * 1000, Math.ceil(totalAfterDiscount / 5000) * 5000, Math.ceil(totalAfterDiscount / 10000) * 10000, Math.ceil(totalAfterDiscount / 20000) * 20000];
    return Array.from(new Set(candidates)).filter(v => v >= totalAfterDiscount).slice(0, 4);
  }, [totalAfterDiscount]);
  // ─── Discount panel handlers ──────────────────────────────────
  const [otherDiscountInput, setOtherDiscountInput] = useState<string>('');
  // ─── Liberar mesa fantasma ────────────────────────────────────────
  // Caso real: la mesa aparece "Ocupada ₡0" en la grilla porque tiene
  // una orden con status activo (new/preparing/ready) pero TODOS sus
  // ítems ya fueron cobrados o cancelados → no hay nada pendiente de
  // cobro. El header de la orden nunca pasó a 'delivered', así que la
  // mesa queda ocupada para siempre. Este botón cierra esas órdenes
  // (status → delivered) SIN registrar ningún pago — los ítems ya
  // estaban cobrados — y libera la mesa. Sólo aplica a mesas reales
  // (no a pedidos de delivery/pickup sintéticos de Caja).
  const [freeingTable, setFreeingTable] = useState(false);
  const isRealTable = !!table?.id && !String(table?.table_number || '').startsWith('order:');
  const handleFreeGhostTable = useCallback(async () => {
    if (!isRealTable || !table?.id || !restaurantId) return;
    setFreeingTable(true);
    try {
      const {
        error
      } = await supabase.from('orders').update({
        status: 'delivered',
        updated_at: new Date().toISOString()
      }).eq('restaurant_id', restaurantId).eq('table_id', table.id).not('status', 'in', '("delivered","cancelled")');
      if (error) throw error;
      onSuccess(true);
      onClose();
    } catch (err) {
      console.error('[NOIZA][PayTableModal] free ghost table error:', err);
    } finally {
      setFreeingTable(false);
    }
  }, [isRealTable, table?.id, restaurantId, onSuccess, onClose]);
  const presetDiscounts = [1000, 2000, 3000, 4000, 5000];
  const applyPresetDiscount = (amt: number) => {
    setDiscountAmount(amt);
    setOtherDiscountInput('');
  };
  const applyOtherDiscount = () => {
    const v = Number(otherDiscountInput.replace(/[^0-9.]/g, '')) || 0;
    setDiscountAmount(v);
  };
  const clearDiscount = () => {
    setDiscountAmount(0);
    setOtherDiscountInput('');
  };
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1472" data-mp="2pfz2izd47ohm">
        <div data-id="element-1473" data-mp="336vj1rtrof8b">
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1474" data-mp="337djl0ngpfm1">
            <Receipt className="w-5 h-5 text-amber-500" data-id="element-1475" data-mp="2z73gbvr213av" />
            Cobrar{' '}
            {table.table_name ? table.table_name : table.table_number && !String(table.table_number).startsWith('order:') ? `Mesa ${table.table_number}` : `Pedido #${String((table.current_order as any)?.order_number || '').split('-').pop() || ''}`}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-1476" data-mp="2et7a1hesp1j2">
            Selecciona los ítems a cobrar (división de cuenta disponible)
          </p>
        </div>
        <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" data-id="element-1477" data-mp="2okuyrajyw4gu">
          <X className="w-5 h-5 text-gray-500" data-id="element-1478" data-mp="2xl0136ab0co5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-1479" data-mp="2xix1pvn9h76r">
        {itemsLoading ? <div className="flex justify-center py-12" data-id="element-1480" data-mp="32i08plmsxnhd">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" data-id="element-1481" data-mp="1381vv13go67o" />
          </div> : unpaidItems.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-1482" data-mp="186lgmy6bjbyg">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" data-id="element-1483" data-mp="10n2ej9ldus00" />
            <p className="font-bold" data-id="element-1484" data-mp="vw3zseg1m1yv">No hay ítems pendientes de cobro</p>
            <p className="text-sm" data-id="element-1485" data-mp="vtr9ne04slaq">Esta mesa ya fue cobrada por completo.</p>
            {isRealTable && <div className="mt-6" data-id="element-1486" data-mp="quekunpp945i">
                <Button variant="primary" onClick={handleFreeGhostTable} disabled={freeingTable} data-id="element-1487" data-mp="lyfs6nxc8zza">
                  {freeingTable ? <span className="flex items-center gap-2" data-id="element-1488" data-mp="1end9anptcqqg">
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-1489" data-mp="19n8qgr1fnjx2" />
                      Liberando…
                    </span> : 'Cerrar y liberar mesa'}
                </Button>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1490" data-mp="3ueiyoqcpsim1">
                  Libera la mesa sin registrar ningún cobro adicional.
                </p>
              </div>}
          </div> : <>
            <button onClick={toggleAll} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1491" data-mp="1i1vbpu1l52we">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900" data-id="element-1492" data-mp="186tk8rju7oqn">
                {allSelected ? <CheckSquare className="w-4 h-4 text-amber-500" data-id="element-1493" data-mp="20to3xm8tyk32" /> : <Square className="w-4 h-4 text-gray-400" data-id="element-1494" data-mp="20ruy2961k3w4" />}
                {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </span>
              <span className="text-xs font-medium text-gray-500" data-id="element-1495" data-mp="1rsgb3jp32bgd">
                {selectedIds.size} / {unpaidItems.length} ítems
              </span>
            </button>

            {/* ── Arma tu plato — PRECIO INDIVIDUAL por línea ──
                Cada línea arma (menu_item_id == null) es un ítem
                independiente: checkbox propio + input de precio propio.
                El cajero elige cuáles cobrar y cuánto vale cada uno.
                Los platillos normales NO aparecen acá. */}
            {(() => {
          const armaItems = unpaidItems.filter((i: any) => isArmaItem(i.order_item_id));
          if (armaItems.length === 0) return null;
          const selectedArmaCount = armaItems.filter((i: any) => selectedIds.has(i.order_item_id)).length;
          return <div className="rounded-xl border-2 border-amber-200 bg-amber-50/30 px-3 py-3" data-id="element-1496" data-mp="3lcrlnsj4rtbt">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1497" data-mp="3wa0vap7jvv2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-id="element-1498" data-mp="3va639vgsio5p">
                      Arma tu plato
                    </span>
                    <span className="text-[11px] font-bold text-gray-600" data-id="element-1499" data-mp="32lgxhj4shunm">
                      {selectedArmaCount} / {armaItems.length}{' '}
                      {armaItems.length === 1 ? 'línea' : 'líneas'}
                    </span>
                  </div>
                  <ul className="space-y-1.5 pl-0" data-id="element-1500" data-mp="3qy2dkcrbdd">
                    {armaItems.map((ai: any) => {
                const isSelected = selectedIds.has(ai.order_item_id);
                const priceStr = armaPriceMap.get(ai.order_item_id) ?? '';
                const priceNum = getArmaPriceNum(ai.order_item_id);
                const needsPrice = isSelected && priceNum <= 0;
                return <li key={ai.order_item_id} className={cn('rounded-lg border px-2.5 py-2 transition-colors', needsPrice ? 'border-red-300 bg-red-50/40' : isSelected ? 'border-emerald-300 bg-emerald-50/40' : 'border-transparent bg-white/50')} data-id="element-1501" data-mp="3ib6omlfacfvl">
                          <div className="flex items-center gap-2" data-id="element-1502" data-mp="2i41ijnh7mp9r">
                            <button type="button" onClick={() => toggleItem(ai.order_item_id)} className="flex items-center gap-2 min-w-0 flex-1 text-left" aria-label={isSelected ? 'Quitar de la cuenta' : 'Agregar a la cuenta'} data-id="element-1503" data-mp="2dc0akdr2rkpj">
                              {isSelected ? <CheckSquare className="w-4 h-4 text-amber-500 shrink-0" data-id="element-1504" data-mp="1pg5m8jvjyzdi" /> : <Square className="w-4 h-4 text-gray-300 shrink-0" data-id="element-1505" data-mp="3jsc9wi5mv8mp" />}
                              <span className={cn('text-sm font-bold truncate', isSelected ? 'text-gray-900' : 'text-gray-500')} data-id="element-1506" data-mp="2smhs69hcjiwm">
                                {ai.item_name}
                              </span>
                            </button>
                            <div className="relative shrink-0" data-id="element-1507" data-mp="2evbm1ujkan0r">
                              <ArmaPriceInput value={priceStr} onChange={val => setArmaPrice(ai.order_item_id, val)} needsPrice={needsPrice} itemName={ai.item_name} data-id="element-1508" data-mp="2ewwhrnhm4c07" />
                            </div>
                          </div>
                          {needsPrice && <p className="text-[10px] text-red-600 font-bold mt-1 pl-6" data-id="element-1509" data-mp="1nm0bw6fc9o1k">
                              Ingresa el monto antes de cobrar.
                            </p>}
                        </li>;
              })}
                  </ul>
                </div>;
        })()}

            <div className="space-y-1.5" data-id="element-1510" data-mp="27b101see5trv">
              {unpaidItems.filter((item: any) => !isArmaItem(item.order_item_id)).map((item: any) => {
            const isSplitMode = splitModeMap.has(item.order_item_id);
            const N = splitModeMap.get(item.order_item_id) || 2;
            const selectedParts = splitSelectedMap.get(item.order_item_id) || new Set();
            const persistedSplit = persistedSplits.get(item.order_item_id);
            const hasPaidParts = persistedSplit && persistedSplit.paidParts.size > 0;
            if (isSplitMode) {
              // effUnitPrice incluye el recargo de opciones (Rancho).
              const unitPrice = Math.round(effUnitPrice(item));
              const paidParts = persistedSplit?.paidParts ?? new Set<number>();
              const paidAmounts = persistedSplit?.paidAmounts ?? new Map<number, number>();
              const amounts = splitAmountsMap.get(item.order_item_id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
              const partsSum = amounts.slice(0, N).reduce((s, v) => s + Math.round(v || 0), 0);
              const sumOk = partsSum === unitPrice;
              return <div key={item.order_item_id} className="w-full flex flex-col gap-2 px-3 py-3 rounded-xl border-2 border-amber-400 bg-amber-50/30 transition-all" data-id="element-1511" data-mp="p23ybhehmbn1">
                        <div className="flex items-start justify-between" data-id="element-1512" data-mp="11jo5gn00kudy">
                          <div data-id="element-1513" data-mp="16awevj1cgfr4">
                            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-1514" data-mp="16cqzsxcm87v7">
                              {item.item_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1" data-id="element-1515" data-mp="dn0nmtujng1q">
                              <span className="text-[11px] text-gray-500" data-id="element-1516" data-mp="dp4zpz2ljld3">
                                Dividir en:
                              </span>
                              <select value={N} disabled={hasPaidParts} onChange={e => {
                        const newN = parseInt(e.target.value, 10);
                        const newSplitModeMap = new Map(splitModeMap);
                        newSplitModeMap.set(item.order_item_id, newN);
                        setSplitModeMap(newSplitModeMap);
                        const newSplitSelectedMap = new Map(splitSelectedMap);
                        const allParts = new Set(Array.from({
                          length: newN
                        }, (_, i) => i));
                        newSplitSelectedMap.set(item.order_item_id, allParts);
                        setSplitSelectedMap(newSplitSelectedMap);
                        // Recalcular montos en división equitativa
                        // al cambiar la cantidad de partes.
                        seedEqualSplit(item.order_item_id, newN);
                      }} className="text-xs border border-amber-300 rounded px-1 py-0.5 bg-white text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50 disabled:bg-transparent disabled:border-transparent disabled:pl-0" data-id="element-1517" data-mp="nk5a6usaxdj2">
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => <option key={n} value={n} data-id="element-1518" data-mp="2saw2stihjv02">
                                      {n} partes
                                    </option>)}
                              </select>
                              <span className="text-[11px] text-gray-500" data-id="element-1519" data-mp="1gxlbod7qkc7v">
                                · Total: {formatCurrency(unitPrice)}
                              </span>
                            </div>
                          </div>
                          {!hasPaidParts && <button type="button" onClick={() => {
                    const newSplitModeMap = new Map(splitModeMap);
                    newSplitModeMap.delete(item.order_item_id);
                    setSplitModeMap(newSplitModeMap);
                    const newSplitSelectedMap = new Map(splitSelectedMap);
                    newSplitSelectedMap.delete(item.order_item_id);
                    setSplitSelectedMap(newSplitSelectedMap);
                  }} className="text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors" data-id="element-1520" data-mp="1x9ot4lt2huro">
                              Cancelar división
                            </button>}
                        </div>
                        <div className="space-y-1 mt-1" data-id="element-1521" data-mp="3baum9g8qfjy1">
                          {Array.from({
                    length: N
                  }).map((_, k) => {
                    const isPaid = paidParts.has(k);
                    const isSelected = selectedParts.has(k);
                    const partAmount = Math.round(amounts[k] ?? 0);
                    return <div key={k} className={cn('w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors', isPaid ? 'border-transparent bg-gray-100 opacity-70' : isSelected ? 'border-amber-300 bg-white' : 'border-transparent hover:bg-white/50')} data-id="element-1522" data-mp="2urp44f8xdny" data-mpi={k}>
                                <button type="button" disabled={isPaid} onClick={() => {
                        if (isPaid) return;
                        const newSelectedParts = new Set(selectedParts);
                        if (isSelected) {
                          newSelectedParts.delete(k);
                        } else {
                          newSelectedParts.add(k);
                        }
                        const newSplitSelectedMap = new Map(splitSelectedMap);
                        newSplitSelectedMap.set(item.order_item_id, newSelectedParts);
                        setSplitSelectedMap(newSplitSelectedMap);
                      }} className={cn('flex items-center gap-2 text-left min-w-0', isPaid && 'cursor-not-allowed')} data-id="element-1523" data-mp="2bjqu1r3309ye">
                                  {isPaid ? <CheckSquare className="w-4 h-4 shrink-0 text-gray-400" data-id="element-1524" data-mp="2v3zflf09w47a" /> : isSelected ? <CheckSquare className="w-4 h-4 shrink-0 text-amber-500" data-id="element-1525" data-mp="2g80b0ecdgm6w" /> : <Square className="w-4 h-4 shrink-0 text-gray-300" data-id="element-1526" data-mp="30o2ww75pld3p" />}
                                  <span className={cn('text-sm font-bold whitespace-nowrap', isPaid ? 'text-gray-500' : isSelected ? 'text-gray-900' : 'text-gray-500')} data-id="element-1527" data-mp="2wmhkemx370u1">
                                    Parte {k + 1}/{N}{' '}
                                    {isPaid && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600" data-id="element-1528" data-mp="23zwwxxjqf8vr">
                                        (Pagada)
                                      </span>}
                                  </span>
                                </button>
                                {isPaid ? <span className="font-bold text-sm tabular-nums text-gray-500 shrink-0" data-id="element-1529" data-mp="1ou0sx38s355c">
                                    {formatCurrency(partAmount)}
                                  </span> : <div className="relative shrink-0" data-id="element-1530" data-mp="115hoiysuncij">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 pointer-events-none" data-id="element-1531" data-mp="117ax4jn0w6t6">
                                      ₡
                                    </span>
                                    <input type="text" inputMode="numeric" value={partAmount} onChange={e => {
                          const raw = Number(e.target.value.replace(/[^0-9]/g, ''));
                          setPartAmount(item.order_item_id, k, raw);
                        }} onFocus={e => e.target.select()} className="w-24 pl-5 pr-2 py-1 rounded-md border-2 border-amber-300 bg-white text-sm font-black tabular-nums text-right text-gray-900 focus:outline-none focus:border-amber-500" aria-label={`Monto parte ${k + 1}`} data-id="element-1532" data-mp="1fudumqbcifgi" />
                                  </div>}
                              </div>;
                  })}
                        </div>
                        {!sumOk && <div className="mt-1 flex items-center gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1.5" data-id="element-1533" data-mp="12pk8mm5efugk">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 text-red-600" data-id="element-1534" data-mp="tqfa3ftu814e" />
                            <p className="text-[11px] font-bold text-red-700 leading-snug" data-id="element-1535" data-mp="yp9qkb4c1dlc">
                              La suma de las partes ({formatCurrency(partsSum)})
                              debe ser igual al total (
                              {formatCurrency(unitPrice)}
                              ).
                            </p>
                          </div>}
                      </div>;
            }
            const isSelected = selectedIds.has(item.order_item_id);
            const remaining = item.remaining_quantity ?? item.quantity;
            const paidSoFar = item.paid_quantity ?? 0;
            const qty = selectedQty.get(item.order_item_id) ?? remaining;
            const lineTotal = effUnitPrice(item) * qty;
            const canDecrement = isSelected && qty > 1;
            const canIncrement = isSelected && qty < remaining;
            const canSplit = item.is_shareable && (item.unit_price || 0) > 0 && remaining >= 1;
            return <div key={item.order_item_id} className={cn('w-full flex items-start gap-3 px-3 py-3 rounded-xl border-2 transition-all', isSelected ? 'border-amber-400 bg-amber-50/50' : 'border-gray-100 hover:border-gray-200 bg-white')} data-id="element-1536" data-mp="2zefneowpfjna">
                      <button type="button" onClick={() => toggleItem(item.order_item_id)} className="mt-0.5 shrink-0" aria-label={isSelected ? 'Quitar de la cuenta' : 'Agregar a la cuenta'} data-id="element-1537" data-mp="oj8tsuxgoc25">
                        {isSelected ? <CheckSquare className="w-5 h-5 text-amber-500" data-id="element-1538" data-mp="15tm4il2pzx3k" /> : <Square className="w-5 h-5 text-gray-300" data-id="element-1539" data-mp="886ymuvb8ilp" />}
                      </button>
                      <div className="flex-1 min-w-0" data-id="element-1540" data-mp="d68g5bd389v2">
                        <button type="button" onClick={() => toggleItem(item.order_item_id)} className="w-full text-left" data-id="element-1541" data-mp="i4th5d05yxkk">
                          <div className="flex items-baseline gap-2 flex-wrap" data-id="element-1542" data-mp="1tjovexuwex0l">
                            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-1543" data-mp="1xksavj91ar1a">
                              {item.item_name}
                            </p>
                            {paidSoFar > 0 && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-1544" data-mp="1r6v13gswmp0l">
                                {paidSoFar} ya cobrado
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-1545" data-mp="2f1xtfyqx3alt">
                            {formatCurrency(Math.round(effUnitPrice(item)))} c/u
                            · quedan {remaining} de {item.quantity}
                          </p>
                          {Array.isArray((item as any).selected_options) && (item as any).selected_options.length > 0 && <div className="mt-0.5 space-y-0.5" data-id="element-1546" data-mp="2xu9tgeu3lzss">
                                {(item as any).selected_options.map((opt: any, idx: number) => <p key={idx} className="text-xs text-amber-700" data-id="element-1547" data-mp="3chi85pmm8n9i" data-mpi={idx}>
                                      · {opt.option_name}
                                    </p>)}
                              </div>}
                          {item.notes && <p className="text-xs text-gray-500 italic mt-0.5" data-id="element-1548" data-mp="2yhzc3v72x2dk">
                              {item.notes}
                            </p>}
                        </button>
                        {canSplit && <div className="mt-2 flex items-center gap-2" data-id="element-1549" data-mp="2ard609gmxs67">
                            <button type="button" onClick={() => {
                    const newSplitModeMap = new Map(splitModeMap);
                    newSplitModeMap.set(item.order_item_id, 2);
                    setSplitModeMap(newSplitModeMap);
                    const newSplitSelectedMap = new Map(splitSelectedMap);
                    newSplitSelectedMap.set(item.order_item_id, new Set([0, 1]));
                    setSplitSelectedMap(newSplitSelectedMap);
                    // Sembramos los montos en división equitativa.
                    seedEqualSplit(item.order_item_id, 2);
                  }} className="text-[11px] font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors" data-id="element-1550" data-mp="2as501ful3hud">
                              Dividir este ítem
                            </button>
                          </div>}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0" data-id="element-1551" data-mp="35uzbahqy2fli">
                        {isSelected && remaining > 1 ? <div className="flex items-center gap-0 bg-white border-2 border-amber-300 rounded-lg overflow-hidden" data-id="element-1552" data-mp="3fk97a8owsiew">
                            <button type="button" onClick={() => setQtyForItem(item.order_item_id, qty - 1)} disabled={!canDecrement} className="w-7 h-7 flex items-center justify-center text-amber-600 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Reducir cantidad" data-id="element-1553" data-mp="3jlb5qnb04qft">
                              <Minus className="w-3.5 h-3.5" data-id="element-1554" data-mp="9x8j7t0211vs" />
                            </button>
                            <span className="w-8 text-center text-sm font-black text-gray-900 tabular-nums" data-id="element-1555" data-mp="4yovpo5tbi1b">
                              {qty}
                            </span>
                            <button type="button" onClick={() => setQtyForItem(item.order_item_id, qty + 1)} disabled={!canIncrement} className="w-7 h-7 flex items-center justify-center text-amber-600 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Aumentar cantidad" data-id="element-1556" data-mp="jp4k1btiz63p">
                              <Plus className="w-3.5 h-3.5" data-id="element-1557" data-mp="1bd69cc19z9tc" />
                            </button>
                          </div> : <span className="text-xs font-bold text-gray-500 tabular-nums" data-id="element-1558" data-mp="1ewomhm1hk06h">
                            {isSelected ? `${qty}×` : `${remaining}×`}
                          </span>}
                        <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-1559" data-mp="14z0u32rcr4rh">
                          {formatCurrency(isSelected ? selectedItems.find(si => si.order_item_id === item.order_item_id)?.pay_line_total ?? 0 : Math.round(effUnitPrice(item) * remaining))}
                        </span>
                      </div>
                    </div>;
          })}
            </div>

            <div className="pt-2" data-id="element-1560" data-mp="2gkfz18vk2b9m">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1561" data-mp="2lc7f2ph34yli">
                Método de pago <span className="text-red-500" data-id="element-1562" data-mp="3nd1dg7bxuidd">*</span>
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-1563" data-mp="3nanidyh9d65t">
                {([{
              id: 'cash',
              label: 'Efectivo',
              Icon: Banknote
            }, {
              id: 'sinpe',
              label: 'SINPE',
              Icon: Smartphone
            }, {
              id: 'card',
              label: 'Tarjeta',
              Icon: CreditCard
            }] as const).map(({
              id,
              label,
              Icon
            }) => <button key={id} onClick={() => setMethod(id)} className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all', method === id ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1564" data-mp="3jbxvnfabyc9i">
                    <Icon className="w-5 h-5 mb-1" data-id="element-1565" data-mp="29cd9xqofk95y" />
                    <span className="text-xs font-bold" data-id="element-1566" data-mp="29b25rpr2ozeb">{label}</span>
                  </button>)}
              </div>
              {!method && <p className="mt-2 text-[11px] font-bold text-amber-600 flex items-center gap-1" data-id="element-1567" data-mp="1zfs4x5ceuy24">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1568" data-mp="1vc2jgor18wlb" />
                  Selecciona el método de pago para poder cobrar.
                </p>}
            </div>

            {/* ─── Solicitar factura (Régimen Simplificado CR) ─── */}
            <div className={cn('rounded-2xl border-2 transition-colors', wantsInvoice ? 'border-gray-900 bg-gray-50' : 'border-gray-100 bg-white')} data-id="element-1569" data-mp="3ks1z8op5pk9x">
              <button type="button" onClick={() => setWantsInvoice(!wantsInvoice)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-id="element-1570" data-mp="1e28frk9vyfj5">
                <div className="flex items-center gap-3" data-id="element-1571" data-mp="143iwpo696p1b">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', wantsInvoice ? 'bg-gray-900 text-white' : 'bg-amber-50 text-amber-600')} data-id="element-1572" data-mp="195rpajf7rp2w">
                    <FileText className="w-5 h-5" data-id="element-1573" data-mp="1my7m32hiuqvd" />
                  </div>
                  <div data-id="element-1574" data-mp="1rtmwtlk8g6nk">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1575" data-mp="1rxapkihusp18">
                      Enviar factura por correo electrónico
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1576" data-mp="gh3zqbnfmdjj">
                      Se enviará por correo electrónico (Régimen Simplificado)
                    </p>
                  </div>
                </div>
                <div className={cn('relative w-11 h-6 rounded-full transition-colors shrink-0', wantsInvoice ? 'bg-gray-900' : 'bg-gray-200')} data-id="element-1577" data-mp="2pnxnkqyh128z">
                  <div className={cn('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all', wantsInvoice ? 'left-[22px]' : 'left-0.5')} data-id="element-1578" data-mp="2zl2et576bnwn" />
                </div>
              </button>

              {wantsInvoice && <div className="px-4 pb-4 space-y-3 border-t border-gray-200 pt-3" data-id="element-1579" data-mp="2fyntj5k2h9i8">
                  {/* ─── ¿Cuándo enviar? ─── */}
                  <div data-id="element-1580" data-mp="1s7iid3p2n5e9">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1581" data-mp="1s6z1lqb5qn1t">
                      ¿Cuándo enviar la factura?
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-1582" data-mp="20uwahdvuy1ln">
                      <button type="button" onClick={() => setSendLater(false)} className={cn('flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all', !sendLater ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300')} data-id="element-1583" data-mp="25wtx8i3gvejc">
                        <Mail className="w-3.5 h-3.5" data-id="element-1584" data-mp="2ykpp99i3ci9s" />
                        Sí, enviar ahora
                      </button>
                      <button type="button" onClick={() => setSendLater(true)} className={cn('flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all', sendLater ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300')} data-id="element-1585" data-mp="z8eziq7ungtw">
                        <Clock className="w-3.5 h-3.5" data-id="element-1586" data-mp="hwgf237pg6a1" />
                        Sí, enviar más tarde
                      </button>
                    </div>
                    {sendLater && <p className="text-[10px] text-amber-700 mt-1.5 leading-snug" data-id="element-1587" data-mp="1akvpsdjt02fk">
                        La solicitud quedará pendiente en{' '}
                        <span className="font-bold" data-id="element-1588" data-mp="10pkb1v8eav7t">
                          Otras Herramientas → Enviar Facturas
                        </span>
                        .
                      </p>}
                  </div>
                  <div data-id="element-1589" data-mp="3ldoxao6ch85a">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1590" data-mp="2dfkrb8r12m2p">
                      A nombre de
                    </label>
                    <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nombre completo del cliente" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1591" data-mp="2hf1z48txfvb1" />
                  </div>
                  {/* Cédula y correo solo se piden cuando se envía AHORA.
                   Si es "más tarde" basta con el nombre — el resto se
                   llena después desde Enviar Facturas. */}
                  {!sendLater && <>
                      <div data-id="element-1592" data-mp="1fs2977bh87m1">
                        <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1593" data-mp="1frao4qfi9sa0">
                          Cédula / Identificación
                          <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1594" data-mp="s4xdmh2t9pkt">
                            (opcional)
                          </span>
                        </label>
                        <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1595" data-mp="114kdgyfw6nbr" />
                      </div>
                      <div data-id="element-1596" data-mp="1ys2ka3pztfd4">
                        <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1597" data-mp="1ytdno2fj0l3p">
                          Correo electrónico
                        </label>
                        <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1598" data-mp="egnb73pymyg5" />
                        <p className="text-[10px] text-gray-500 mt-1" data-id="element-1599" data-mp="p8skxzysz9m">
                          Puedes dejar los datos en blanco y completarlos en el
                          siguiente paso si el cliente aún no los tiene.
                        </p>
                      </div>
                    </>}
                  {sendLater && <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1600" data-mp="3940u4xa5t43o">
                      Solo necesitamos el nombre para registrar la solicitud.
                      Cédula y correo se completan después desde{' '}
                      <span className="font-bold" data-id="element-1601" data-mp="1r8i3f61vz72z">Enviar Facturas</span>.
                    </p>}
                </div>}
            </div>

            {/* ─── Imprimir comprobante con datos del cliente ─── */}
            {/* Independiente del envío por correo. Controla si el ticket
                impreso por el bridge muestra nombre + cédula del cliente.
                Si está apagado, la factura impresa sale igual que hoy. */}
            <div className={cn('rounded-2xl border-2 transition-colors', showCustomerOnInvoice ? 'border-cyan-300 bg-cyan-50/40' : 'border-gray-100 bg-white')} data-id="element-1602" data-mp="17i4wtk4tijoy">
              <label className="w-full flex items-center justify-between px-4 py-3 cursor-pointer" data-id="element-1603" data-mp="tj5a4i0v8evf">
                <div className="flex items-center gap-3" data-id="element-1604" data-mp="olwzd2i1rd47">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', showCustomerOnInvoice ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-600')} data-id="element-1605" data-mp="ndk2txdgxrr8">
                    <Receipt className="w-5 h-5" data-id="element-1606" data-mp="9czjn03n5tx7" />
                  </div>
                  <div data-id="element-1607" data-mp="4f7qy3v6a3ry">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1608" data-mp="4fh8d325j2m1">
                      Imprimir comprobante con datos del cliente
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1609" data-mp="3qt7kaaz7aeg6">
                      Agrega nombre y cédula al comprobante impreso.
                    </p>
                  </div>
                </div>
                <input type="checkbox" checked={showCustomerOnInvoice} onChange={e => setShowCustomerOnInvoice(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer" data-id="element-1610" data-mp="3htknk2hf1wtg" />
              </label>
              {showCustomerOnInvoice && <div className="px-4 pb-4 space-y-3 border-t border-cyan-100 pt-3" data-id="element-1611" data-mp="21cvancdy9msz">
                  <div data-id="element-1612" data-mp="21aspb5nqshul">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1613" data-mp="26c89kvi3ikbx">
                      Nombre del cliente
                    </label>
                    <input type="text" value={invoiceCustomerName} onChange={e => setInvoiceCustomerName(e.target.value)} placeholder="Nombre completo del cliente" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1614" data-mp="1dn10j2l0mccf" />
                  </div>
                  <div data-id="element-1615" data-mp="1y7gxi26qtl6c">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1616" data-mp="1y6xc2bhvwenk">
                      Cédula / Identificación
                      <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1617" data-mp="1t69pc20oujz5">
                        (opcional)
                      </span>
                    </label>
                    <input type="text" value={invoiceCustomerId} onChange={e => setInvoiceCustomerId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1618" data-mp="22yp8v3rmvgm5" />
                  </div>
                </div>}
            </div>

            {/* ─── Asignar descuento (universal, todos los KDS) ─── */}
            <div className={cn('rounded-2xl border-2 transition-colors', showDiscountPanel || discountAmount > 0 ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-100 bg-white')} data-id="element-1619" data-mp="2up3fcexbhqns">
              <button type="button" onClick={() => setShowDiscountPanel(!showDiscountPanel)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-id="element-1620" data-mp="2ta0pxiuszl9b">
                <div className="flex items-center gap-3" data-id="element-1621" data-mp="3qvydvvrycisn">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', discountAmount > 0 ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600')} data-id="element-1622" data-mp="3qvewcg67rwf5">
                    <span className="text-lg font-black" data-id="element-1623" data-mp="dgs2c8wlitxx">%</span>
                  </div>
                  <div data-id="element-1624" data-mp="2l0mw9elmcprb">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1625" data-mp="2l0v8d49x3r1g">
                      Asignar descuento
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1626" data-mp="2gxghtjocrs9r">
                      {discountAmount > 0 ? `Aplicado: -${formatCurrency(discountAmount)}` : 'Resta un monto al total a cobrar'}
                    </p>
                  </div>
                </div>
                <div className={cn('text-xs font-black uppercase tracking-wider', showDiscountPanel ? 'text-emerald-700' : 'text-gray-400')} data-id="element-1627" data-mp="34nswdde3sn9b">
                  {showDiscountPanel ? 'Cerrar' : 'Abrir'}
                </div>
              </button>

              {showDiscountPanel && <div className="px-4 pb-4 space-y-3 border-t border-emerald-200 pt-3" data-id="element-1628" data-mp="3nfvgwztv8nou">
                  <div data-id="element-1629" data-mp="3inc3c2v6hsk1">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1630" data-mp="3in2ckd2b7hda">
                      Montos rápidos
                    </label>
                    <div className="grid grid-cols-5 gap-2" data-id="element-1631" data-mp="3uzo0topp08hw">
                      {presetDiscounts.map(amt => {
                  const active = discountAmount === amt;
                  return <button key={amt} type="button" onClick={() => applyPresetDiscount(amt)} className={cn('px-2 py-2 rounded-lg border-2 text-xs font-black transition-colors tabular-nums', active ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-emerald-200 bg-white text-emerald-700 hover:border-emerald-400')} data-id="element-1632" data-mp="8szey7k2my20">
                            ₡{amt.toLocaleString('es-CR')}
                          </button>;
                })}
                    </div>
                  </div>
                  <div data-id="element-1633" data-mp="2slyfbtodqir0">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1634" data-mp="36bigww6859yq">
                      Otro monto
                    </label>
                    <div className="flex gap-2" data-id="element-1635" data-mp="31ipbe62xlxll">
                      <div className="relative flex-1" data-id="element-1636" data-mp="3g8wwyuo8jwqi">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base font-black text-emerald-600 pointer-events-none" data-id="element-1637" data-mp="3gbj2ypvha46m">
                          ₡
                        </span>
                        <input type="text" inputMode="decimal" value={otherDiscountInput} onChange={e => setOtherDiscountInput(e.target.value.replace(/[^0-9.]/g, ''))} onBlur={applyOtherDiscount} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyOtherDiscount();
                    }
                  }} placeholder="Ingrese el monto" className="w-full pl-8 pr-3 py-2 rounded-lg border-2 border-emerald-200 focus:border-emerald-500 focus:outline-none text-sm font-bold text-gray-900 placeholder:text-gray-300 bg-white tabular-nums" data-id="element-1638" data-mp="3p81upyrz3m1m" />
                      </div>
                      <button type="button" onClick={applyOtherDiscount} className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black transition-colors" data-id="element-1639" data-mp="t0qv49hljght">
                        Aplicar
                      </button>
                    </div>
                  </div>
                  {discountAmount > 0 && <div className="flex items-center justify-between rounded-lg bg-emerald-100 border border-emerald-300 px-3 py-2" data-id="element-1640" data-mp="3r1e6hyjdte6i">
                      <span className="text-xs font-bold text-emerald-800" data-id="element-1641" data-mp="3i8akh1uo7t09">
                        Descuento actual:{' '}
                        <span className="tabular-nums" data-id="element-1642" data-mp="3d9hhi3d0fkec">
                          -{formatCurrency(discountAmount)}
                        </span>
                      </span>
                      <button type="button" onClick={clearDiscount} className="text-[11px] font-black uppercase tracking-wider text-emerald-700 hover:text-emerald-900" data-id="element-1643" data-mp="33b1mvqerxt03">
                        Quitar
                      </button>
                    </div>}
                </div>}
            </div>

            {/* ─── Cash received + change (only for cash method) ─── */}
            {method === 'cash' && selectedItems.length > 0 && <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-4 space-y-3" data-id="element-1644" data-mp="2kcyjtvb5kgyp">
                <div className="flex items-center justify-between" data-id="element-1645" data-mp="2p6b4xvgmi5mp">
                  <p className="text-xs font-black text-amber-700 uppercase tracking-wider" data-id="element-1646" data-mp="2t61trtmlabol">
                    💵 Paga con (efectivo)
                  </p>
                  <p className="text-[10px] text-amber-600 font-medium" data-id="element-1647" data-mp="21fxc42q7s2r2">
                    opcional — calcula el vuelto
                  </p>
                </div>
                <div className="relative" data-id="element-1648" data-mp="25fz6dqtmdtwb">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-black text-amber-500 pointer-events-none" data-id="element-1649" data-mp="2agdef8v813uc">
                    ₡
                  </span>
                  <input type="text" inputMode="decimal" value={cashReceived} onChange={e => setCashReceived(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-2xl font-black text-gray-900 placeholder:text-gray-300 bg-white tabular-nums" data-id="element-1650" data-mp="3gz1rtfcf2hza" />
                </div>
                {quickCashAmounts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1651" data-mp="231hfafkf6ga">
                    {quickCashAmounts.map(amt => <button key={amt} type="button" onClick={() => setCashReceived(String(amt))} className={cn('px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors tabular-nums', cashReceivedNum === amt ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-200 bg-white text-amber-700 hover:border-amber-400')} data-id="element-1652" data-mp="3k9pj0qbepbh7">
                        {formatCurrency(amt)}
                      </button>)}
                  </div>}
                {cashReceivedNum > 0 && <div className={cn('rounded-xl px-4 py-3 flex items-baseline justify-between', hasEnoughCash ? 'bg-emerald-500 text-white' : 'bg-red-50 border-2 border-red-200')} data-id="element-1653" data-mp="3qp64e628z0dc">
                    <span className={cn('text-xs font-black uppercase tracking-wider', hasEnoughCash ? 'text-white/90' : 'text-red-600')} data-id="element-1654" data-mp="3vpaoe62d6d8b">
                      {hasEnoughCash ? 'Vuelto a entregar' : 'Falta'}
                    </span>
                    <span className={cn('text-2xl font-black tabular-nums', hasEnoughCash ? 'text-white' : 'text-red-700')} data-id="element-1655" data-mp="2r0qaso1ykofr">
                      {formatCurrency(hasEnoughCash ? change : Math.abs(change))}
                    </span>
                  </div>}
              </div>}

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-id="element-1656" data-mp="3dwgss0cn242t">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-1657" data-mp="3np60ck52ixsv" />
                {error}
              </div>}
          </>}
      </div>

      {unpaidItems.length > 0 && <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl" data-id="element-1658" data-mp="1h6e7d57mxqm">
          {discountAmount > 0 && <div className="mb-3 space-y-1" data-id="element-1659" data-mp="1m0id3nio1ygd">
              <div className="flex justify-between items-baseline text-xs" data-id="element-1660" data-mp="1lx48e8mj5d5r">
                <span className="font-bold text-gray-500 uppercase tracking-wider" data-id="element-1661" data-mp="1gz2mzh1mt1r4">
                  Subtotal
                </span>
                <span className="font-bold text-gray-700 tabular-nums" data-id="element-1662" data-mp="1727txdo4ntk8">
                  {formatCurrency(total)}
                </span>
              </div>
              <div className="flex justify-between items-baseline text-xs" data-id="element-1663" data-mp="24pf67o2bukx0">
                <span className="font-bold text-emerald-600 uppercase tracking-wider" data-id="element-1664" data-mp="1zrwszlha1jj7">
                  Descuento
                </span>
                <span className="font-black text-emerald-600 tabular-nums" data-id="element-1665" data-mp="1uy2dku8pfqpx">
                  -{formatCurrency(discountAmount)}
                </span>
              </div>
            </div>}
          <div className="flex justify-between items-baseline mb-3" data-id="element-1666" data-mp="2nij1fdv715tq">
            <div data-id="element-1667" data-mp="2ioy13f64nzrd">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1668" data-mp="2ioeswq6a4pqj">
                {isPartial ? 'Total parcial' : 'Total a cobrar'}
              </p>
              {isPartial && <p className="text-[10px] text-amber-600 font-bold" data-id="element-1669" data-mp="2884aqjnb0vj1">
                  Cuenta dividida — quedará saldo pendiente
                </p>}
            </div>
            <span className="text-3xl font-black text-amber-500 tabular-nums" data-id="element-1670" data-mp="1y9eq4j40wwz3">
              {formatCurrency(totalAfterDiscount)}
            </span>
          </div>
          {hasIncompleteArmaPrices && <div className="mb-3 rounded-xl border-2 border-red-200 bg-red-50 px-3 py-2 flex items-start gap-2" data-id="element-1671" data-mp="2qwrduwlx7atz">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" data-id="element-1672" data-mp="2c84os36hr4pk" />
              <p className="text-xs font-bold text-red-700 leading-snug" data-id="element-1673" data-mp="2h66dp4qlly8y">
                Ingresa el precio del plato{' '}
                <span className="uppercase" data-id="element-1674" data-mp="34uz3skkjhbvn">Arma tu plato</span> antes de
                cobrar.
              </p>
            </div>}
          <Button fullWidth size="lg" onClick={onSubmit} disabled={submitting || selectedItems.length === 0 || hasIncompleteArmaPrices || hasInvalidSplit || !method} className="bg-gray-900 hover:bg-gray-800 text-white font-bold shadow-lg" data-id="element-1675" data-mp="y89t6qtohr1v">
            {submitting ? <span className="flex items-center justify-center gap-2" data-id="element-1676" data-mp="faqizjvhi2dv">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-1677" data-mp="abdvqy0patar" />
                Procesando...
              </span> : <span className="flex items-center justify-center gap-2" data-id="element-1678" data-mp="obexefosi4ra">
                <Banknote className="w-5 h-5" data-id="element-1679" data-mp="k8se5366ps5p" />
                Cobrar
              </span>}
          </Button>
        </div>}
    </>;
}
// ─── STEP 2: Payment confirmed — choose: invoice or close ─────────────────
function PaidStep(props: {
  payment: PaymentRecord;
  allPaid: boolean;
  autoInvoiceResult?: {
    ok: boolean;
    msg: string;
    email: string;
  } | null;
  autoInvoiceSending?: boolean;
  isRancho?: boolean;
  onPrintComanda?: () => void;
  printingComanda?: boolean;
  printComandaResult?: {
    ok: boolean;
    msg: string;
  } | null;
  onClose: () => void;
  onGoInvoice: () => void;
  onChargeRemaining: () => void;
}) {
  const {
    payment,
    allPaid,
    autoInvoiceResult,
    autoInvoiceSending,
    isRancho,
    onPrintComanda,
    printingComanda,
    printComandaResult,
    onClose,
    onGoInvoice,
    onChargeRemaining
  } = props;
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1680" data-mp="nfhxsky81fn5">
        <div className="flex items-center gap-3" data-id="element-1681" data-mp="3mzlw0zmeicv1">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-1682" data-mp="3r2i59zq1wwmb">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-1683" data-mp="3jk0u7p2j19r8" />
          </div>
          <div data-id="element-1684" data-mp="3ekxos3imx1iq">
            <h2 className="text-xl font-black text-gray-900" data-id="element-1685" data-mp="3ekfv9encx1ei">
              Pago registrado
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1686" data-mp="3ai1ceujou1n8">
              {methodLabel} · {formatCurrency(payment.amount)}
              {!allPaid && ' · Saldo pendiente en mesa'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1687" data-mp="30mhhaacy76zj">
          <X className="w-5 h-5 text-gray-500" data-id="element-1688" data-mp="2vs3l45qgovtt" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-1689" data-mp="2qvmriw0htp9c">
        <div className="text-center mb-6" data-id="element-1690" data-mp="2mu1jpp2tg160">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-4" data-id="element-1691" data-mp="2mvcp1tbwtgz8">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" data-id="element-1692" data-mp="2hwhvbwcem80c" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-1" data-id="element-1693" data-mp="2cvu6nhbne39a">
            ¡Cobro exitoso!
          </h3>
          {autoInvoiceSending ? <p className="text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-1694" data-mp="9lgv887fpsb6">
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-1695" data-mp="3rrc1dj9rou99" />
              Enviando factura por correo…
            </p> : autoInvoiceResult ? <p className={cn('text-sm font-medium', autoInvoiceResult.ok ? 'text-emerald-600' : 'text-amber-700')} data-id="element-1696" data-mp="jpnkxtc3beo">
              {autoInvoiceResult.ok ? '✓ ' : '⚠ '}
              {autoInvoiceResult.msg}
            </p> : <p className="text-sm text-gray-500" data-id="element-1697" data-mp="392owp1vi66w7">¿El cliente desea factura?</p>}
        </div>

        {autoInvoiceResult?.ok && <div className="max-w-sm mx-auto mb-4 rounded-xl px-4 py-3 text-sm font-medium border bg-emerald-50 border-emerald-200 text-emerald-700 text-center" data-id="element-1698" data-mp="2z2y1exz4bnv2">
            Comprobante enviado a{' '}
            <span className="font-bold" data-id="element-1699" data-mp="2kfpitc3hcg92">{autoInvoiceResult.email}</span>.
          </div>}

        <div className="max-w-sm mx-auto space-y-2" data-id="element-1700" data-mp="2fh4htagelsjk">
          {isRancho && onPrintComanda ? <>
              <button onClick={onPrintComanda} disabled={printingComanda} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-1701" data-mp="2ueom0f5h7joo">
                {printingComanda ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-1702" data-mp="3d567pzrw5f0j" />
                    Enviando…
                  </> : <>
                    <Printer className="w-5 h-5" data-id="element-1703" data-mp="387mun36xj8a6" />
                    Imprimir comanda
                  </>}
              </button>
              {printComandaResult && <div className={cn('rounded-xl px-3 py-2 text-xs font-bold text-center', printComandaResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200')} data-id="element-1704" data-mp="3i5kjnswm593u">
                  {printComandaResult.msg}
                </div>}
            </> : <button onClick={onGoInvoice} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors" data-id="element-1705" data-mp="cdc9oj28g2ir">
              <FileText className="w-5 h-5" data-id="element-1706" data-mp="mbs2cqgwrjud" />
              Sí, generar factura
            </button>}
          {!allPaid ? <button onClick={onChargeRemaining} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold transition-colors" data-id="element-1707" data-mp="2o3mz7zyi9jsj">
              <ArrowLeft className="w-4 h-4" data-id="element-1708" data-mp="yo3x44k1floj" />
              Cobrar saldo restante
            </button> : <button onClick={onClose} className="w-full py-3 rounded-xl border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold transition-colors" data-id="element-1709" data-mp="13l2poio1zkkm">
              {isRancho ? 'Cerrar' : 'No, cerrar'}
            </button>}
        </div>
      </div>
    </>;
}
// ─── STEP 3: Generate invoice — collect customer data + send by email ────
function InvoiceStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo;
  payment: PaymentRecord;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const {
    table,
    billingInfo,
    payment,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    onBack,
    onClose
  } = props;
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const invoiceUrl = `${window.location.origin}/factura/${payment.id}`;
  const buildEmailBody = () => {
    const greeting = customerName.trim() || 'Cliente';
    return `Estimad@ ${greeting},\n\n` + `Agradecemos profundamente su visita. Esperamos que su paso por nuestras instalaciones haya sido de su agrado.\n\n` + (customerLegalId.trim() ? `Datos del cliente:\n` + `Nombre: ${greeting}\n` + `Cédula / Identificación: ${customerLegalId.trim()}\n\n` : '') + `Adjunto podrá encontrar la factura solicitada:\n` + `${invoiceUrl}\n\n` + `Factura N° ${consecStr} · Total: ${new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(payment.amount)}\n\n` + `¡Lo esperamos pronto!\n\n` + `Att: ${billingInfo.business_name}`;
  };
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  const handleSendEmail = async () => {
    if (!customerEmail.trim()) {
      alert('Ingresa el correo electrónico del cliente.');
      return;
    }
    // ─── Defensive validation ─────────────────────────────────────────
    // payment viene de InvoiceStep props → completedPayment → result.payment
    // de processPayment(), que hace INSERT INTO payments ... RETURNING *.
    // Por lo tanto payment.id ES el UUID de payments.id (NO order.id, NO
    // table.id, NO consecutive_number).
    const paymentId = payment?.id;
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // Logs temporales para validar exactamente qué se manda
    console.log('[NOIZA][invoice] payment object:', payment);
    console.log('[NOIZA][invoice] payment_id being sent:', paymentId);
    console.log('[NOIZA][invoice] payment_id is valid UUID?', UUID_RE.test(String(paymentId || '')));
    if (!paymentId || !UUID_RE.test(String(paymentId))) {
      setSendResult({
        ok: false,
        msg: `payment_id inválido (${paymentId}). Debería ser el UUID de payments.id. Reportá este caso.`
      });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const {
        supabase
      } = await import('../../lib/supabase');
      const {
        data,
        error
      } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          payment_id: paymentId,
          customer_email: customerEmail.trim(),
          customer_name: customerName.trim() || null,
          customer_legal_id: customerLegalId.trim() || null
        }
      });
      console.log('[NOIZA][invoice] invoke response:', {
        data,
        error
      });
      if (error) {
        // FunctionsHttpError exposes .context with the response body
        let serverError = error.message || 'Error invocando la función';
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            serverError = body?.error || body?.message || serverError;
          } else if (ctx?.error) {
            serverError = ctx.error;
          }
        } catch {
          /* swallow */
        }
        setSendResult({
          ok: false,
          msg: `No se pudo enviar: ${serverError}. Revisá los logs de la edge function en Supabase.`
        });
        setSending(false);
        return;
      }
      if (data?.success) {
        setSendResult({
          ok: true,
          msg: `✓ Comprobante enviado a ${customerEmail.trim()}. Revisá la bandeja de entrada (y la carpeta de Spam si tarda).`
        });
        setSending(false);
        return;
      }
      setSendResult({
        ok: false,
        msg: `Respuesta inesperada: ${JSON.stringify(data)}`
      });
    } catch (err: any) {
      console.error('[NOIZA][invoice] handleSendEmail exception:', err);
      setSendResult({
        ok: false,
        msg: `Error: ${err?.message || 'desconocido'}`
      });
    } finally {
      setSending(false);
    }
  };
  const handlePrint = () => {
    openPrintableInvoice({
      info: billingInfo,
      payment,
      tableLabel: table.table_name || `Mesa ${table.table_number}`,
      customer: {
        name: customerName.trim() || null,
        legal_id: customerLegalId.trim() || null,
        email: customerEmail.trim() || null
      }
    });
  };
  const canSend = customerName.trim() && customerEmail.trim();
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1710" data-mp="34pohlspqjlap">
        <div className="flex items-center gap-3" data-id="element-1711" data-mp="2zoha7uri8o4k">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1712" data-mp="2uq7f1k5s5niy">
            <ArrowLeft className="w-4 h-4 text-gray-600" data-id="element-1713" data-mp="1a73l8fi7gntu" />
          </button>
          <div data-id="element-1714" data-mp="1f66ru4du323x">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1715" data-mp="1f5olug1dd9it">
              <FileText className="w-5 h-5 text-gray-700" data-id="element-1716" data-mp="1k5t0e0ipvu6e" />
              Generar factura
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1717" data-mp="1o6msl636zv53">
              Factura N° {consecStr} · {formatCurrency(payment.amount)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1718" data-mp="wg8jdn64j6zr">
          <X className="w-5 h-5 text-gray-500" data-id="element-1719" data-mp="10h0t0hiwrdwk" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-1720" data-mp="159btblhk91pn">
        <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl px-4 py-3" data-id="element-1721" data-mp="15awkr28kw4ja">
          Ingresa los datos del cliente para emitir la factura. Se enviará por
          correo electrónico con un enlace al comprobante.
        </p>

        <div data-id="element-1722" data-mp="32bcinl9ymage">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1723" data-mp="32at7cnetekbc">
            Nombre completo *
          </label>
          <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ej. María Pérez Rojas" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1724" data-mp="3c9a2zxop7n3q" />
        </div>

        <div data-id="element-1725" data-mp="3uzrr1oyl5uir">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1726" data-mp="3uy6xo2o0chmh">
            Cédula / Identificación
            <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1727" data-mp="3q0x90p8g09zp">
              (opcional)
            </span>
          </label>
          <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1728" data-mp="3t786siopswt" />
        </div>

        <div data-id="element-1729" data-mp="ife0x3sne9h1">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1730" data-mp="ieusqessuzg7">
            Correo electrónico *
          </label>
          <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1731" data-mp="3on6rp3l2yar" />
        </div>

        {sendResult && <div className={cn('rounded-xl px-4 py-3 text-sm font-medium border', sendResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700')} data-id="element-1732" data-mp="3lw388s021n7r">
            {sendResult.msg}
          </div>}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-2" data-id="element-1733" data-mp="3s7sq7e970gfm">
        <Button variant="outline" onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 font-bold" data-id="element-1734" data-mp="61x6slvhz9c2">
          <Printer className="w-4 h-4" data-id="element-1735" data-mp="azfek8xs3iih" />
          Imprimir
        </Button>
        <Button onClick={handleSendEmail} disabled={!canSend || sending} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1736" data-mp="fsruzvsb0j05">
          {sending ? <>
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-1737" data-mp="ts9k6c9ihya1" />
              Enviando…
            </> : <>
              <Mail className="w-4 h-4" data-id="element-1738" data-mp="13tc0s69mjmam" />
              Enviar por correo
            </>}
        </Button>
      </div>
    </>;
}